import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { MapContainer, TileLayer, Marker, Circle, Popup, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from '../hooks/useTranslation';
import {
  FiMapPin,
  FiPlus,
  FiLayers,
  FiTarget,
  FiSettings,
  FiSave,
  FiX,
  FiLoader,
  FiEdit2,
  FiTrash2,
  FiMoreVertical,
  FiSearch,
  FiZap,
  FiInfo,
  FiUpload,
  FiAlertTriangle,
  FiCheck,
  FiImage,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiCheckSquare,
  FiSquare,
  FiToggleLeft,
  FiToggleRight,
  FiEyeOff,
  FiEye,
  FiRotateCcw,
  FiCopy,
  FiStar,
  FiArrowDown,
  FiGrid,
  FiKey,
  FiRefreshCw,
  FiNavigation,
  FiArrowUp,
  FiMap
} from 'react-icons/fi';
import QRCode from 'qrcode';
import { challengesAPI, routeChallengesAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import NFTImageGalleryModal from '../components/NFTImageGalleryModal';
import GalleryImagePicker from '../components/GalleryImagePicker';
import NFTIconGalleryModal from '../components/NFTIconGalleryModal';
import CreateChallengeModal from '../components/CreateChallengeModal';
import { LucideIcon } from '../components/IconPicker';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Map = () => {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState([52.520007, 13.404954]); // Berlin center
  const [mapZoom, setMapZoom] = useState(10);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [editChallengeFormData, setEditChallengeFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingChallenge, setDeletingChallenge] = useState(null);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingChallenge, setPermanentDeletingChallenge] = useState(null);
  const [newChallengeLocation, setNewChallengeLocation] = useState(null);
  const [createChallengeFormData, setCreateChallengeFormData] = useState({
    name: '',
    type: 'urban',
    description: '',
    radius: 100,
    difficulty: 'Easy',
    points: '10',
    latitude: '',
    longitude: '',
    criteria: {},
    nftRewardTemplate: '',
    location: '',
    image_url: '',
    icon_emoji: '🏆',
    badge_color: '#E5E7EB'
  });
  const [userLocation, setUserLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [isRepositioningMarker, setIsRepositioningMarker] = useState(false);
  const [repositionedLocation, setRepositionedLocation] = useState(null);
  const [tempMarkerLocation, setTempMarkerLocation] = useState(null);
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [repositioningChallenge, setRepositioningChallenge] = useState(null); // Store challenge during repositioning
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showRefinementModal, setShowRefinementModal] = useState(false);
  const [showGeneratorModal, setShowGeneratorModal] = useState(false);
  const [generatedSuggestions, setGeneratedSuggestions] = useState(null);
  const [showSuggestionsMap, setShowSuggestionsMap] = useState(true); // Show map of AI results by default
  const [generating, setGenerating] = useState(false);
  const [regionInput, setRegionInput] = useState('');
  const [countryInput, setCountryInput] = useState('Germany'); // Default country for AI generation
  const [radiusKm, setRadiusKm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [addedChallengeIndices, setAddedChallengeIndices] = useState(new Set());
  const [keyInsights, setKeyInsights] = useState([]);
  const [editingGeneratedChallenge, setEditingGeneratedChallenge] = useState(null);
  const [showEditGeneratedModal, setShowEditGeneratedModal] = useState(false);
  const [showNFTImageGallery, setShowNFTImageGallery] = useState(false);
  const [imageGalleryTarget, setImageGalleryTarget] = useState(null); // 'edit' or 'generated'
  const [showNFTIconGallery, setShowNFTIconGallery] = useState(false);
  const [iconGalleryTarget, setIconGalleryTarget] = useState(null); // 'edit' or 'generated'

  // Bulk selection states
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState(new Set());

  // Filter states
  // filterTypes removed - now using filterCategories only
  const [filterCategories, setFilterCategories] = useState([]);

  const [filterLocation, setFilterLocation] = useState('');
  const [filterDifficulties, setFilterDifficulties] = useState([]);
  const [filterPointsMin, setFilterPointsMin] = useState('');
  const [filterPointsMax, setFilterPointsMax] = useState('');
  const [filterTimeRestricted, setFilterTimeRestricted] = useState(null); // null = all, true = only time-restricted, false = only unrestricted
  const [filterActiveStatus, setFilterActiveStatus] = useState(null); // null = all, true = only active, false = only inactive
  const [filterRated, setFilterRated] = useState(null); // null = all, true = only rated, false = only unrated
  const [filterVerification, setFilterVerification] = useState(null); // null = all, 'qr' = QR types, 'passphrase' = passphrase types
  const [filterRouteMode, setFilterRouteMode] = useState(null); // null = all, 'route' = only routes, 'single' = only single point
  const [sortBy, setSortBy] = useState('newest'); // newest, name, points, completions, rating
  const [showDeleted, setShowDeleted] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [filterHiddenGems, setFilterHiddenGems] = useState(false);

  // Route waypoints cache
  const [routeWaypointsCache, setRouteWaypointsCache] = useState({});
  // Waypoint pick-from-map mode
  const [isPickingWaypoints, setIsPickingWaypoints] = useState(false);
  const [pickedWaypoints, setPickedWaypoints] = useState([]);
  const [waypointPickCallback, setWaypointPickCallback] = useState(null);

  const mapRef = useRef();
  const searchInputRef = useRef();
  const queryClient = useQueryClient();

  // Dynamic categories from API
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/challenge-categories');
        if (response.data.success) {
          setCategories(response.data.categories || []);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setCategoriesLoaded(true);
      }
    };
    fetchCategories();
  }, []);

  // SVG paths for Lucide icons (for HTML string contexts like Leaflet markers)
  const iconSvgPaths = {
    'map-pin': '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
    'trees': '<path d="M10 10v.2A3 3 0 0 1 8.9 16v0H5v0h0a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z"/><path d="M7 16v6"/><path d="M13 19v3"/><path d="M16 13v.2A3 3 0 0 1 14.9 19v0H11v0h0a3 3 0 0 1-1-5.8V13a3 3 0 0 1 6 0Z"/>',
    'landmark': '<line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/>',
    'utensils': '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
    'mountain': '<path d="m8 3 4 8 5-5 5 15H2L8 3z"/>',
    'building': '<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>',
    'waves': '<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>',
    'trophy': '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
    'users': '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    'palette': '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/>',
    'moon': '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
    'camera': '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>',
    'anchor': '<circle cx="12" cy="5" r="3"/><line x1="12" x2="12" y1="22" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>',
    'wine': '<path d="M8 22h8"/><path d="M7 10h10"/><path d="M12 15v7"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z"/>',
    'bike': '<circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/>',
    'bird': '<path d="M16 7h.01"/><path d="M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20"/><path d="m20 7 2 .5-2 .5"/><path d="M10 18v3"/><path d="M14 17.75V21"/><path d="M7 18a6 6 0 0 0 3.84-10.61"/>',
    'tent': '<path d="M3.5 21 14 3"/><path d="M20.5 21 10 3"/><path d="M15.5 21 12 15l-3.5 6"/><path d="M2 21h20"/>',
    'compass': '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
    'footprints': '<path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"/><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"/><path d="M16 17h4"/><path d="M4 13h4"/>',
    'leaf': '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>',
    'sun': '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
    'snowflake': '<line x1="2" x2="22" y1="12" y2="12"/><line x1="12" x2="12" y1="2" y2="22"/><path d="m20 16-4-4 4-4"/><path d="m4 8 4 4-4 4"/><path d="m16 4-4 4-4-4"/><path d="m8 20 4-4 4 4"/>',
    'fish': '<path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z"/><path d="M18 12v.5"/><path d="M16 17.93a9.77 9.77 0 0 1 0-11.86"/><path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .23 6.5-1.24 1.5-1.24 5-.23 6.5C5.58 18.03 7 16 7 13.33"/><path d="M10.46 7.26C10.2 5.88 9.17 4.24 8 3h5.8a2 2 0 0 1 1.98 1.67l.23 1.4"/><path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 0 1.49-3.98"/>',
    'dog': '<path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2.823.47-4.113 6.006-4 7 .08.703 1.725 1.722 3.656 1 1.261-.472 1.96-1.45 2.344-2.5"/><path d="M14.267 5.172c0-1.39 1.577-2.493 3.5-2.172 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"/><path d="M8 14v.5"/><path d="M16 14v.5"/><path d="M11.25 16.25h1.5L12 17l-.75-.75Z"/><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444c0-1.061-.162-2.2-.493-3.309m-9.243-6.082A8.801 8.801 0 0 1 12 5c.78 0 1.5.108 2.161.306"/>',
    'music': '<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>',
    'book': '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>',
    'coffee': '<path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" x2="6" y1="2" y2="4"/><line x1="10" x2="10" y1="2" y2="4"/><line x1="14" x2="14" y1="2" y2="4"/>',
    'ice-cream': '<path d="m7 11 4.08 10.35a1 1 0 0 0 1.84 0L17 11"/><path d="M17 7A5 5 0 0 0 7 7"/><path d="M17 7a2 2 0 0 1 0 4H7a2 2 0 0 1 0-4"/>',
    'church': '<path d="m18 7 4 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9l4-2"/><path d="M14 22v-4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v4"/><path d="M18 22V5l-6-3-6 3v17"/><path d="M12 7v5"/><path d="M10 9h4"/>',
    'castle': '<path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"/><path d="M18 11V4H6v7"/><path d="M15 22v-4a3 3 0 0 0-6 0v4"/><path d="M22 11V9"/><path d="M2 11V9"/><path d="M6 4V2"/><path d="M18 4V2"/><path d="M10 4V2"/><path d="M14 4V2"/>',
    'train': '<rect width="6" height="16" x="4" y="3" rx="2"/><rect width="6" height="16" x="14" y="3" rx="2"/><path d="M4 11h16"/><path d="M12 3v8"/><path d="m8 19-2 3"/><path d="m18 22-2-3"/><path d="m6 22 2-3"/><path d="m16 19 2 3"/>',
    'plane': '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>',
    'ship': '<path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><path d="M12 10v4"/><path d="M12 2v3"/>',
    'car': '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>',
    'umbrella': '<path d="M22 12a10.06 10.06 1 0 0-20 0Z"/><path d="M12 12v8a2 2 0 0 0 4 0"/><path d="M12 2v1"/>',
    'star': '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    'heart': '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
    'flag': '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
    'home': '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
    'shopping-bag': '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
    'gift': '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>',
    'droplet': '<path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/>'
  };

  // Helper to generate inline SVG for Leaflet markers
  const getIconSvg = (iconName, color = 'white', size = 16) => {
    const path = iconSvgPaths[iconName] || iconSvgPaths['map-pin'];
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
  };

  // Legacy challenge type configurations (fallback for old data)
  const legacyChallengeTypes = {
    urban: { color: '#3B82F6', name: 'Urban', lucideIcon: 'building' },
    nature: { color: '#22C55E', name: 'Nature', lucideIcon: 'trees' },
    historic: { color: '#A855F7', name: 'Historic', lucideIcon: 'landmark' },
    adventure: { color: '#EF4444', name: 'Adventure', lucideIcon: 'mountain' },
    water: { color: '#06B6D4', name: 'Water', lucideIcon: 'waves' },
    park: { color: '#22C55E', name: 'Park', lucideIcon: 'trees' },
    food: { color: '#F97316', name: 'Food', lucideIcon: 'utensils' },
    sports: { color: '#EAB308', name: 'Sports', lucideIcon: 'trophy' },
    nightlife: { color: '#6366F1', name: 'Nightlife', lucideIcon: 'moon' },
    photography: { color: '#14B8A6', name: 'Photography', lucideIcon: 'camera' },
    museums: { color: '#8B5CF6', name: 'Museums', lucideIcon: 'palette' }
  };

  // Build challengeTypes from categories (for backwards compatibility with existing code)
  const challengeTypes = React.useMemo(() => {
    const types = {};
    categories.forEach(cat => {
      types[cat.slug] = {
        id: cat.id,
        color: cat.color,
        name: cat.name,
        slug: cat.slug,
        lucideIcon: cat.icon // Lucide icon name for both React components and SVG generation
      };
    });
    // Merge with legacy types for fallback
    return { ...legacyChallengeTypes, ...types };
  }, [categories]);

  // Helper to get category info from challenge (supports both old type and new category)
  const getCategoryInfo = (challenge) => {
    // First try to use the new category object
    if (challenge.category) {
      return {
        id: challenge.category.id,
        name: challenge.category.name,
        slug: challenge.category.slug,
        color: challenge.category.color,
        lucideIcon: challenge.category.icon // Lucide icon name
      };
    }
    // Fall back to legacy type
    const legacyType = challengeTypes[challenge.type] || challengeTypes.urban || legacyChallengeTypes.urban;
    return {
      name: legacyType.name,
      slug: challenge.type,
      color: legacyType.color,
      lucideIcon: legacyType.lucideIcon
    };
  };

  // Get challenge icon (individual or group)
  const getChallengeIcon = (challenge) => {
    const config = challengeTypes[challenge.type] || challengeTypes.urban;
    
    // Check if challenge has individual custom icon in metadata
    if (challenge.metadata && challenge.metadata.customIcon) {
      // Determine the correct base URL for icons
      const hostname = window.location.hostname;
      let baseUrl;
      
      if (hostname === 'cms.exploreandwin.com' || hostname === 'exploreandwin.com') {
        baseUrl = 'https://api.exploreandwin.com';
      } else {
        baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';
      }
      
      const customIconUrl = `${baseUrl}${challenge.metadata.customIcon.path}`;
      
      return {
        svgPath: customIconUrl,
        color: config.color,
        hasIndividualIcon: true
      };
    }
    
    // Return group icon as fallback
    return {
      svgPath: config.svgPath,
      color: config.color,
      hasIndividualIcon: false
    };
  };

  // Helper function to render challenge icon using LucideIcon
  const renderChallengeIcon = (challenge, size = 24, className = "") => {
    const catInfo = getCategoryInfo(challenge);
    return (
      <LucideIcon
        name={catInfo.lucideIcon || 'map-pin'}
        size={size}
        color="white"
        className={className}
      />
    );
  };

  // Helper function to render type icon (for dropdowns and forms)
  const renderTypeIcon = (type, size = 24, className = "") => {
    const config = challengeTypes[type] || challengeTypes.urban;
    return (
      <LucideIcon
        name={config.lucideIcon || 'map-pin'}
        size={size}
        color={config.color}
        className={className}
      />
    );
  };

  // Legacy renderTypeIcon with img fallback (kept for backwards compatibility)
  const renderTypeIconImg = (type, size = 24, className = "") => {
    const config = challengeTypes[type] || challengeTypes.urban;
    return (
      <img
        src={config.svgPath}
        alt={type}
        className={`inline-block ${className}`}
        style={{ width: size, height: size }}
        onError={(e) => {
          e.target.style.display = 'none';
          const fallback = document.createElement('span');
          fallback.innerHTML = '📍';
          fallback.style.fontSize = `${size}px`;
          fallback.style.display = 'inline-block';
          e.target.parentNode.appendChild(fallback);
        }}
      />
    );
  };

  // Fetch challenges - ignore bounds when category filter is active to show all challenges in that category
  const hasCategoryFilter = filterCategories.length > 0;
  // Serialize filterCategories to string for stable query key comparison
  const categoryFilterKey = filterCategories.length > 0 ? filterCategories.slice().sort().join(',') : '';
  const { data: challengesData, isLoading: challengesLoading } = useQuery(
    ['map-challenges', hasCategoryFilter ? 'all-categories' : mapBounds, showDeleted, categoryFilterKey],
    async () => {
      const response = await challengesAPI.getChallenges({
        // When category filter is active, don't use bounds - fetch all challenges to show filtered results everywhere
        bounds: hasCategoryFilter ? undefined : (mapBounds ? JSON.stringify(mapBounds) : undefined),
        limit: 500,
        page: 1,
        includeInactive: true,
        includeDeleted: showDeleted
      });
      return response.data;
    },
    {
      enabled: true,
      staleTime: 30000
    }
  );

  // Filter challenges based on selected filters
  const filteredChallenges = React.useMemo(() => {
    if (!challengesData?.challenges) return [];

    const filtered = challengesData.challenges.filter(challenge => {
      // Filter by category (replaces old type filter)
      if (filterCategories.length > 0) {
        const challengeCategorySlug = challenge.category?.slug;
        if (!challengeCategorySlug || !filterCategories.includes(challengeCategorySlug)) {
          return false;
        }
      }

      // Filter by location (search in name and description)
      if (filterLocation.trim() !== '') {
        const searchTerm = filterLocation.toLowerCase();
        const matchesName = challenge.name?.toLowerCase().includes(searchTerm);
        const matchesDescription = challenge.description?.toLowerCase().includes(searchTerm);
        if (!matchesName && !matchesDescription) {
          return false;
        }
      }

      // Filter by difficulty
      if (filterDifficulties.length > 0 && !filterDifficulties.includes(challenge.difficulty)) {
        return false;
      }

      // Filter by points range
      const points = parseInt(challenge.points) || 0;
      if (filterPointsMin !== '' && points < parseInt(filterPointsMin)) {
        return false;
      }
      if (filterPointsMax !== '' && points > parseInt(filterPointsMax)) {
        return false;
      }

      // Filter by time restriction
      if (filterTimeRestricted === true && !challenge.timeConstraint) {
        return false;
      }
      if (filterTimeRestricted === false && challenge.timeConstraint) {
        return false;
      }

      // Filter by active status
      if (filterActiveStatus === true && challenge.isActive === false) {
        return false;
      }
      if (filterActiveStatus === false && challenge.isActive !== false) {
        return false;
      }

      // Filter by rating status
      if (filterRated === true && (!challenge.totalRatings || challenge.totalRatings === 0)) {
        return false;
      }
      if (filterRated === false && challenge.totalRatings > 0) {
        return false;
      }

      // Filter by verification type
      if (filterVerification === 'qr' && !['qr_code', 'gps_qr'].includes(challenge.verificationType)) {
        return false;
      }
      if (filterVerification === 'passphrase' && !['passphrase', 'gps_passphrase'].includes(challenge.verificationType)) {
        return false;
      }

      // Filter by hidden gems
      if (filterHiddenGems && !challenge.hiddenGem) {
        return false;
      }

      // Filter by route mode
      if (filterRouteMode === 'route' && challenge.challengeMode !== 'multi_point') {
        return false;
      }
      if (filterRouteMode === 'single' && challenge.challengeMode === 'multi_point') {
        return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'points':
          return (b.points || 0) - (a.points || 0);
        case 'completions':
          return (b.stats?.completions || 0) - (a.stats?.completions || 0);
        case 'rating':
          return (b.averageRating || 0) - (a.averageRating || 0);
        case 'newest':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    return filtered;
  }, [challengesData, filterCategories, filterLocation, filterDifficulties, filterPointsMin, filterPointsMax, filterTimeRestricted, filterActiveStatus, filterRated, filterVerification, filterHiddenGems, filterRouteMode, sortBy]);

  // Create challenge mutation
  const createChallengeMutation = useMutation(
    async (challengeData) => {
      const response = await challengesAPI.createChallenge(challengeData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('map-challenges');
        setShowCreateModal(false);
        setNewChallengeLocation(null);
        toast.success('Challenge created successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create challenge');
      }
    }
  );

  // Update challenge mutation
  const updateChallengeMutation = useMutation(
    async ({ id, challengeData }) => {
      const response = await challengesAPI.updateChallenge(id, challengeData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('map-challenges');
        setShowEditModal(false);
        setEditingChallenge(null);
        toast.success('Challenge updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update challenge');
      }
    }
  );

  // Delete challenge mutation
  const deleteChallengeMutation = useMutation(
    async (challengeId) => {
      const response = await challengesAPI.deleteChallenge(challengeId);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('map-challenges');
        setShowDeleteModal(false);
        setDeletingChallenge(null);
        setSelectedChallenge(null);
        toast.success('Challenge deleted successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete challenge');
      }
    }
  );

  // Restore challenge mutation
  const restoreChallengeMutation = useMutation(
    async (challengeId) => {
      const response = await challengesAPI.restoreChallenge(challengeId);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('map-challenges');
        toast.success(`Challenge "${data.challenge.name}" restored successfully!`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to restore challenge');
      }
    }
  );

  // Clone challenge mutation
  const cloneChallengeMutation = useMutation(
    async (challengeId) => {
      const response = await challengesAPI.cloneChallenge(challengeId);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('map-challenges');
        toast.success(`Challenge cloned as "${data.challenge.name}"`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to clone challenge');
      }
    }
  );

  // Permanent delete mutation
  const permanentDeleteMutation = useMutation(
    async (challengeId) => {
      const response = await challengesAPI.permanentDeleteChallenge(challengeId);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('map-challenges');
        setShowPermanentDeleteModal(false);
        setPermanentDeletingChallenge(null);
        setSelectedChallenge(null);
        toast.success(`Challenge "${data.challenge.name}" permanently deleted`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to permanently delete challenge');
      }
    }
  );

  // Bulk activate/deactivate handler
  const handleBulkToggleActive = async (isActive) => {
    if (selectedChallengeIds.size === 0) return;
    try {
      await challengesAPI.bulkUpdateActive([...selectedChallengeIds], isActive);
      queryClient.invalidateQueries('map-challenges');
      toast.success(`${selectedChallengeIds.size} challenge(s) ${isActive ? 'activated' : 'deactivated'}`);
      setSelectedChallengeIds(new Set());
      setBulkSelectMode(false);
    } catch (error) {
      toast.error('Failed to update challenges');
    }
  };

  // Toggle individual challenge selection
  const toggleChallengeSelection = (challengeId) => {
    setSelectedChallengeIds(prev => {
      const next = new Set(prev);
      if (next.has(challengeId)) {
        next.delete(challengeId);
      } else {
        next.add(challengeId);
      }
      return next;
    });
  };

  // Select/deselect all filtered challenges (excluding deleted)
  const selectableChallenges = filteredChallenges.filter(c => !c.isDeleted);
  const toggleSelectAll = () => {
    if (selectedChallengeIds.size === selectableChallenges.length) {
      setSelectedChallengeIds(new Set());
    } else {
      setSelectedChallengeIds(new Set(selectableChallenges.map(c => c.id)));
    }
  };

  // CSV export (excludes deleted challenges)
  const exportToCSV = () => {
    const challenges = filteredChallenges.filter(c => !c.isDeleted);
    if (challenges.length === 0) {
      toast.error('No challenges to export');
      return;
    }
    const headers = ['Name', 'Type', 'Difficulty', 'Points', 'Latitude', 'Longitude', 'Radius', 'Active', 'Completions', 'Avg Rating', 'Total Ratings', 'Description'];
    const rows = challenges.map(c => [
      `"${(c.name || '').replace(/"/g, '""')}"`,
      c.type,
      c.difficulty,
      c.points,
      c.location?.latitude,
      c.location?.longitude,
      c.location?.radius,
      c.isActive !== false ? 'Yes' : 'No',
      c.stats?.completions || 0,
      c.averageRating?.toFixed(1) || '',
      c.totalRatings || 0,
      `"${(c.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `challenges_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${challenges.length} challenges`);
  };

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          setUserLocation([latitude, longitude]);
          setLocationAccuracy(accuracy);
        },
        (error) => {
          console.warn('Location access denied:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    }
  }, []);

  // Fetch waypoints for selected route challenge
  useEffect(() => {
    if (selectedChallenge?.challengeMode === 'multi_point' && !routeWaypointsCache[selectedChallenge.id]) {
      const fetchWaypoints = async () => {
        try {
          const response = await routeChallengesAPI.getWaypoints(selectedChallenge.id);
          const wps = response.data?.waypoints || [];
          setRouteWaypointsCache(prev => ({ ...prev, [selectedChallenge.id]: wps }));
        } catch (err) {
          console.error('Failed to fetch waypoints:', err);
        }
      };
      fetchWaypoints();
    }
  }, [selectedChallenge, routeWaypointsCache]);

  // Geocoding service using backend proxy (to avoid CORS issues)
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      // REACT_APP_API_URL already includes /api, so don't add it again
      const token = localStorage.getItem('cms_accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(
        `${API_URL}/locations/geocode/search?q=${encodeURIComponent(query)}&limit=5`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const formattedResults = data.map(item => ({
        id: item.place_id,
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        type: item.type,
        address: {
          country: item.address?.country,
          state: item.address?.state,
          city: item.address?.city || item.address?.town || item.address?.village,
          street: item.address?.road,
          house_number: item.address?.house_number
        }
      }));
      
      setSearchResults(formattedResults);
      setShowSearchDropdown(formattedResults.length > 0);
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Failed to search location');
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input changes with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        searchLocation(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Handle search result selection
  const handleLocationSelect = (result) => {
    if (mapRef.current && result.lat && result.lon) {
      mapRef.current.setView([result.lat, result.lon], 15);
      setSearchQuery(result.display_name.split(',')[0]); // Set first part as search query
      setShowSearchDropdown(false);
      toast.success(`Jumped to ${result.address.city || result.display_name.split(',')[0]}`);
    }
  };

  // Generate challenge suggestions
  const handleOpenRefinement = async () => {
    setAddedChallengeIndices(new Set()); // Reset added challenges for new generation
    setShowRefinementModal(true);

    // Fetch statistical insights to show in refinement modal
    try {
      const token = localStorage.getItem('cms_accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      const response = await fetch(`${API_URL}/analytics/generate-challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          region: 'Berlin and Dessau region, Germany',
          category: 'all',
          difficulty: 'all',
          useAI: false // Only get statistical analysis, no AI generation
        })
      });

      if (response.ok) {
        const data = await response.json();
        setKeyInsights(data.suggestions?.slice(0, 3) || []);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  };

  const handleGenerateChallenges = async () => {
    // Keep modal open, just show generating state
    setGenerating(true);
    try {
      const token = localStorage.getItem('cms_accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      // Build region string with country and radius
      // Include country for accurate geocoding (e.g., "Hamburg Hbf, Germany")
      let locationWithCountry = regionInput;
      if (countryInput && countryInput.trim() && !regionInput.toLowerCase().includes(countryInput.toLowerCase())) {
        locationWithCountry = `${regionInput}, ${countryInput}`;
      }

      let regionString = locationWithCountry;
      if (radiusKm && radiusKm.trim()) {
        regionString = `${radiusKm}km radius around ${locationWithCountry}`;
      }

      const response = await fetch(`${API_URL}/analytics/generate-challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          region: regionString,
          category: selectedCategories.length > 0 ? selectedCategories : 'all',
          difficulty: selectedDifficulties.length > 0 ? selectedDifficulties : 'all',
          useAI: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Server returned ${response.status}`);
      }

      const data = await response.json();
      setGeneratedSuggestions(data);
      setShowRefinementModal(false); // Close refinement modal
      setShowGeneratorModal(true); // Open results modal
      toast.success(`Generated ${data.aiChallenges?.length || 0} AI challenges!`);
    } catch (error) {
      console.error('Error generating challenges:', error);
      toast.error(error.message || 'Failed to generate challenge suggestions');
    } finally {
      setGenerating(false);
    }
  };

  // Add individual challenge to database
  const handleAddChallenge = async (challenge, index) => {
    try {
      const token = localStorage.getItem('cms_accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      const response = await fetch(`${API_URL}/challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: challenge.name,
          description: challenge.description,
          type: challenge.category,
          difficulty: challenge.difficulty,
          points: challenge.points,
          latitude: challenge.latitude,
          longitude: challenge.longitude,
          radius: challenge.radius || 50, // Default 50m radius
          active: true,
          // NFT Visual Customization
          location: challenge.locationName || challenge.landmark || null,  // Use locationName or landmark as fallback
          image_url: challenge.image_url || null,
          icon_emoji: challenge.icon_emoji || '🏆',
          badge_color: challenge.badge_color || '#E5E7EB' // Light grey default
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create challenge');
      }

      // Mark challenge as added
      setAddedChallengeIndices(prev => new Set([...prev, index]));

      // Update the total count in generatedSuggestions
      setGeneratedSuggestions(prev => ({
        ...prev,
        analysis: {
          ...prev.analysis,
          totalExistingChallenges: prev.analysis.totalExistingChallenges + 1
        }
      }));

      await queryClient.invalidateQueries('challenges');
      toast.success(`Added: ${challenge.name}`);
      return true;
    } catch (error) {
      console.error('Error adding challenge:', error);
      toast.error(`Failed to add: ${challenge.name}`);
      return false;
    }
  };

  // Open edit modal for generated challenge
  const handleEditGeneratedChallenge = (challenge, index) => {
    setEditingGeneratedChallenge({
      ...challenge,
      index,
      radius: 50 // Default radius
    });
    setShowEditGeneratedModal(true);
  };

  // Save edited generated challenge to database
  const handleSaveEditedChallenge = async () => {
    if (!editingGeneratedChallenge) return;

    const success = await handleAddChallenge(editingGeneratedChallenge, editingGeneratedChallenge.index);
    if (success) {
      setShowEditGeneratedModal(false);
      setEditingGeneratedChallenge(null);
    }
  };

  // Add all challenges to database
  const handleAddAllChallenges = async () => {
    if (!generatedSuggestions?.aiChallenges) return;

    const challenges = generatedSuggestions.aiChallenges;
    const remainingChallenges = challenges.filter((_, index) => !addedChallengeIndices.has(index));

    if (remainingChallenges.length === 0) {
      toast.info('All challenges have already been added!');
      return;
    }

    let successCount = 0;

    toast.loading(`Adding ${remainingChallenges.length} challenges...`);

    for (let i = 0; i < challenges.length; i++) {
      if (!addedChallengeIndices.has(i)) {
        const success = await handleAddChallenge(challenges[i], i);
        if (success) successCount++;
      }
    }

    toast.dismiss();
    if (successCount === remainingChallenges.length) {
      toast.success(`Successfully added all ${successCount} challenges!`);
      setShowGeneratorModal(false);
      setAddedChallengeIndices(new Set()); // Reset for next generation
    } else {
      toast.success(`Added ${successCount} of ${remainingChallenges.length} challenges`);
    }
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Custom marker icons
  const createCustomIcon = (challenge) => {
    const iconInfo = getChallengeIcon(challenge);
    const catInfo = getCategoryInfo(challenge);
    const iconSvg = getIconSvg(catInfo.lucideIcon || 'map-pin', 'white', 16);
    return L.divIcon({
      className: 'custom-challenge-marker',
      html: `
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          overflow: hidden;
        ">
          <img src="${iconInfo.svgPath}"
               alt="${challenge.type}"
               style="width: 100%; height: 100%; object-fit: cover;"
               onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
          />
          <div style="
            background: ${catInfo.color};
            width: 100%;
            height: 100%;
            display: none;
            align-items: center;
            justify-content: center;
          ">
            ${iconSvg}
          </div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  // Map event handler component
  const MapEventHandler = () => {
    useMapEvents({
      moveend: (e) => {
        const bounds = e.target.getBounds();
        const zoom = e.target.getZoom();
        setMapZoom(zoom);
        setMapBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      },
      zoomend: (e) => {
        setMapZoom(e.target.getZoom());
      },
      ready: (e) => {
        // Set initial bounds and zoom when map is ready
        const bounds = e.target.getBounds();
        const zoom = e.target.getZoom();
        setMapZoom(zoom);
        setMapBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      },
      click: (e) => {
        // Handle waypoint pick-from-map mode
        if (isPickingWaypoints) {
          const lat = parseFloat(e.latlng.lat.toFixed(5));
          const lng = parseFloat(e.latlng.lng.toFixed(5));
          setPickedWaypoints(prev => [...prev, { lat, lng }]);
          return;
        }

        // Handle marker repositioning mode
        if (isRepositioningMarker) {
          const newLat = parseFloat(e.latlng.lat.toFixed(5));
          const newLng = parseFloat(e.latlng.lng.toFixed(5));
          setTempMarkerLocation([newLat, newLng]);
          // Don't set showLocationConfirm, the popup will open automatically
          return;
        }

        // Close any open detail modals when clicking the map
        if (selectedChallenge) {
          setSelectedChallenge(null);
        }

        // If create mode is active, set new challenge location
        if (showCreateModal) {
          setNewChallengeLocation([e.latlng.lat, e.latlng.lng]);
        }
      },
      dblclick: (e) => {
        // Double-click to add new challenge - reset form data first
        setCreateChallengeFormData({
          name: '',
          type: 'urban',
          description: '',
          radius: 100,
          difficulty: 'Easy',
          points: '10',
          latitude: '',
          longitude: '',
          criteria: {},
          nftRewardTemplate: '',
          location: '',
          image_url: '',
          icon_emoji: '🏆',
          badge_color: '#E5E7EB'
        });
        setNewChallengeLocation([e.latlng.lat, e.latlng.lng]);
        setShowCreateModal(true);
      }
    });
    return null;
  };

  // Challenge Detail Modal Component
  const ChallengeDetailModal = ({ isOpen, onClose, challenge, challengeTypes }) => {
    // ESC key handler
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onClose]);

    if (!isOpen || !challenge) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                  style={{ backgroundColor: challengeTypes[challenge.type]?.color, color: 'white' }}
                >
                  {renderChallengeIcon(challenge, 32)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{challenge.name}</h2>
                  <p className="text-gray-600">{challengeTypes[challenge.type]?.name}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Challenge Image */}
            {challenge.imageUrl && (
              <div className="mb-6">
                <img
                  src={challenge.imageUrl}
                  alt={challenge.name}
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
              <p className="text-gray-700">{challenge.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiMapPin className="w-4 h-4 text-gray-500" />
                  <div className="font-medium text-gray-900">{t('cms.map.location')}</div>
                </div>
                <div className="text-sm text-gray-600">
                  {challenge.location.latitude.toFixed(6)}, {challenge.location.longitude.toFixed(6)}
                </div>
                <div className="text-sm text-gray-500">{t('cms.map.radius')}: {challenge.location.radius}m</div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiTarget className="w-4 h-4 text-gray-500" />
                  <div className="font-medium text-gray-900">Statistics</div>
                </div>
                <div className="text-sm text-gray-600">{challenge.stats?.completions || 0} {t('cms.map.completions')}</div>
                <div className="text-sm text-gray-500">{challenge.stats?.nftsMinted || 0} NFTs minted</div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiSettings className="w-4 h-4 text-gray-500" />
                  <div className="font-medium text-gray-900">Details</div>
                </div>
                <div className="text-sm text-gray-600">{new Date(challenge.createdAt).toLocaleDateString()}</div>
                <div className="text-sm text-gray-500">By {challenge.createdBy || 'System'}</div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingChallenge(challenge);
                  setShowEditModal(true);
                  onClose();
                }}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <FiEdit2 className="w-4 h-4" />
                <span>{t('cms.map.editChallenge')}</span>
              </button>
              <button
                onClick={() => {
                  setDeletingChallenge(challenge);
                  setShowDeleteModal(true);
                  onClose();
                }}
                className="btn btn-danger flex items-center space-x-2"
              >
                <FiTrash2 className="w-4 h-4" />
                <span>{t('cms.common.delete')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const jumpToUserLocation = () => {
    if (userLocation && mapRef.current) {
      mapRef.current.setView(userLocation, 15);
    } else {
      toast.error('Location not available. Please enable location access.');
    }
  };

  // Ratings Breakdown Component (used in edit modal)
  const RatingsBreakdown = ({ challengeId }) => {
    const [ratingsData, setRatingsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (!challengeId) return;
      const fetchRatings = async () => {
        try {
          const response = await challengesAPI.getChallengeRatings(challengeId, { limit: 5, sort: 'newest' });
          setRatingsData(response.data);
        } catch (err) {
          console.error('Failed to fetch ratings:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchRatings();
    }, [challengeId]);

    if (loading) return <p className="text-xs text-gray-400">Loading ratings...</p>;
    if (!ratingsData || ratingsData.pagination.total === 0) return null;

    return (
      <div>
        {/* Star breakdown bars */}
        <div className="space-y-1 mb-3">
          {[5,4,3,2,1].map(star => {
            const count = ratingsData.breakdown[star] || 0;
            const pct = ratingsData.pagination.total > 0 ? (count / ratingsData.pagination.total) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-gray-600">{star}</span>
                <span className="text-amber-400">★</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-amber-400 h-full rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-6 text-right text-gray-500">{count}</span>
              </div>
            );
          })}
        </div>

        {/* Recent reviews */}
        {ratingsData.ratings.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Recent Reviews</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {ratingsData.ratings.map(r => (
                <div key={r.id} className="border border-gray-100 rounded p-2 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-700">{r.user.displayName}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-amber-400">{'★'.repeat(r.rating)}</span>
                      <span className="text-gray-300">{'★'.repeat(5 - r.rating)}</span>
                    </div>
                  </div>
                  {r.review && <p className="text-gray-600">{r.review}</p>}
                  <p className="text-gray-400 mt-1">{new Date(r.createdAt).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Edit Challenge Modal Component
  // Edit Challenge Modal - SIMPLIFIED VERSION (matching NFTTemplateDesigner pattern)
  const EditChallengeModal = ({ isOpen, onClose, challenge, externalFormData }) => {
    const [formData, setFormData] = useState({
      name: challenge?.name || '',
      type: challenge?.type || 'urban',
      description: challenge?.description || '',
      radius: challenge?.location?.radius || 100,
      difficulty: challenge?.difficulty || 'Easy',
      points: challenge?.points || 10,
      latitude: challenge?.location?.latitude ? parseFloat(challenge.location.latitude).toFixed(5) : '',
      longitude: challenge?.location?.longitude ? parseFloat(challenge.location.longitude).toFixed(5) : '',
      criteria: challenge?.criteria || {},
      nftRewardTemplate: challenge?.nftReward || challenge?.nftRewardTemplate || '',
      location: challenge?.locationName || '',
      image_url: challenge?.imageUrl || challenge?.image_url || '',
      icon_emoji: challenge?.iconEmoji || challenge?.icon_emoji || '🏆',
      badge_color: challenge?.badgeColor || challenge?.badge_color || '#E5E7EB'
    });

    // NFT card image URL (generated based on challenge image/title)
    const nftCardImageUrl = challenge?.nftImageUrl || null;

    const [customIcon, setCustomIcon] = useState(null);
    const [iconPreview, setIconPreview] = useState(null);
    const fileInputRef = useRef(null);

    // Image upload state
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const imageFileInputRef = useRef(null);

    // Sync formData when challenge prop changes (when modal opens with different challenge)
    useEffect(() => {
      if (isOpen && challenge) {
        setFormData({
          name: challenge.name || '',
          type: challenge.type || 'urban',
          description: challenge.description || '',
          radius: challenge.location?.radius || 100,
          difficulty: challenge.difficulty || 'Easy',
          points: challenge.points || 10,
          latitude: challenge.location?.latitude ? parseFloat(challenge.location.latitude).toFixed(5) : '',
          longitude: challenge.location?.longitude ? parseFloat(challenge.location.longitude).toFixed(5) : '',
          criteria: challenge.criteria || {},
          nftRewardTemplate: challenge.nftReward || challenge.nftRewardTemplate || '',
          location: challenge.locationName || '',
          image_url: challenge.imageUrl || challenge.image_url || '',
          icon_emoji: challenge.iconEmoji || challenge.icon_emoji || '🏆',
          badge_color: challenge.badgeColor || challenge.badge_color || '#E5E7EB'
        });
        console.log('[EditChallenge] Loaded challenge:', {
          id: challenge.id,
          imageUrl: challenge.imageUrl,
          image_url: challenge.image_url,
          nftRewardTemplate: challenge.nftReward || challenge.nftRewardTemplate
        });
      }
    }, [isOpen, challenge?.id]);

    // Time constraint state
    const existingTC = challenge?.timeConstraint || null;
    const [tcEnabled, setTcEnabled] = useState(!!existingTC);
    const [tcType, setTcType] = useState(existingTC?.type || 'window');
    const [tcStartHour, setTcStartHour] = useState(existingTC?.start_hour ?? 9);
    const [tcEndHour, setTcEndHour] = useState(existingTC?.end_hour ?? 18);
    const [tcDays, setTcDays] = useState(existingTC?.days_of_week || []);
    const [tcDateStart, setTcDateStart] = useState(existingTC?.start_date || '');
    const [tcDateEnd, setTcDateEnd] = useState(existingTC?.end_date || '');
    const [tcTimezone, setTcTimezone] = useState(existingTC?.timezone || 'Europe/Berlin');
    const [showTcSection, setShowTcSection] = useState(!!existingTC);

    // Category state
    const [selectedCategoryId, setSelectedCategoryId] = useState(
      challenge?.category?.id || ''
    );

    // Sync category when challenge changes or modal opens
    useEffect(() => {
      if (isOpen) {
        if (challenge?.category?.id) {
          setSelectedCategoryId(challenge.category.id);
        } else if (categories.length > 0) {
          // Default to first enabled category if challenge has no category
          const defaultCat = categories.find(c => c.isDefault) || categories[0];
          if (defaultCat) {
            setSelectedCategoryId(defaultCat.id);
          }
        }
      }
    }, [isOpen, challenge?.category?.id, categories]);

    // Verification state
    const existingVType = challenge?.verificationType || 'gps';
    const existingMeta = challenge?.metadata || {};
    const [showVerifSection, setShowVerifSection] = useState(existingVType !== 'gps');
    const [verifType, setVerifType] = useState(existingVType);
    const [verifQrSecret, setVerifQrSecret] = useState(existingMeta.qr_secret || '');
    const [verifQrDataUrl, setVerifQrDataUrl] = useState('');
    const [verifPassphrase, setVerifPassphrase] = useState(existingMeta.passphrase || '');
    const [verifPassphraseHint, setVerifPassphraseHint] = useState(existingMeta.passphrase_hint || '');

    // Route challenge state
    const [editChallengeMode, setEditChallengeMode] = useState(challenge?.challengeMode || 'single_point');
    const [editRouteType, setEditRouteType] = useState(challenge?.routeType || 'ordered');
    const [editMinRequired, setEditMinRequired] = useState(challenge?.routeMinRequired || 3);
    const [editWaypoints, setEditWaypoints] = useState([]);
    const [editWaypointsLoaded, setEditWaypointsLoaded] = useState(false);

    // Generate QR image for edit modal
    useEffect(() => {
      if (['qr_code', 'gps_qr'].includes(verifType) && verifQrSecret) {
        QRCode.toDataURL(verifQrSecret, { width: 200, margin: 2 })
          .then(url => setVerifQrDataUrl(url))
          .catch(err => console.error('QR generation error:', err));
      }
    }, [verifType, verifQrSecret]);

    // Auto-generate QR secret if needed
    useEffect(() => {
      if (['qr_code', 'gps_qr'].includes(verifType) && !verifQrSecret) {
        setVerifQrSecret(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 12));
      }
    }, [verifType, verifQrSecret]);

    // Load waypoints for route challenges
    useEffect(() => {
      if (isOpen && challenge?.challengeMode === 'multi_point' && !editWaypointsLoaded) {
        const loadWaypoints = async () => {
          try {
            const response = await routeChallengesAPI.getWaypoints(challenge.id);
            const wps = (response.data?.waypoints || []).map(wp => ({
              name: wp.name || '',
              latitude: String(wp.latitude),
              longitude: String(wp.longitude),
              radius: wp.radius || 30,
              isRequired: wp.isRequired !== false
            }));
            setEditWaypoints(wps);
            setEditWaypointsLoaded(true);
          } catch (err) {
            console.error('Failed to load waypoints:', err);
          }
        };
        loadWaypoints();
      }
    }, [isOpen, challenge, editWaypointsLoaded]);

    // Language/Translation state
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState('en');
    const [translations, setTranslations] = useState({});

    // Fetch available languages when modal opens
    useEffect(() => {
      if (isOpen) {
        const fetchLanguages = async () => {
          try {
            const response = await api.get('/cms/settings/languages');
            const langs = response.data.languages?.filter(l => l.enabled) || [];
            setAvailableLanguages(langs.length > 0 ? langs : [{ code: 'en', name: 'English', isDefault: true }]);
            const defaultLang = langs.find(l => l.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLang);
          } catch (error) {
            console.error('Failed to fetch languages:', error);
            setAvailableLanguages([{ code: 'en', name: 'English', isDefault: true }]);
          }
        };
        fetchLanguages();

        // Initialize translations from challenge data
        if (challenge?.translations) {
          setTranslations(challenge.translations);
        } else {
          // Initialize with default language using current values
          setTranslations({
            en: { name: challenge?.name || '', description: challenge?.description || '' }
          });
        }
      }
    }, [isOpen, challenge]);

    // Update formData when external data changes (from gallery selections)
    useEffect(() => {
      if (externalFormData && isOpen) {
        setFormData(prev => ({ ...prev, ...externalFormData }));
      }
    }, [externalFormData, isOpen]);

    // ESC key handler to close modal
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isOpen, onClose]);

    // Handle icon upload
    const handleIconUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.type.match(/^image\/(svg\+xml|png|jpeg|jpg)$/)) {
          toast.error('Please select an SVG, PNG, or JPG file');
          return;
        }
        
        // Validate file size (max 1MB)
        if (file.size > 1024 * 1024) {
          toast.error('File size must be less than 1MB');
          return;
        }
        
        setCustomIcon(file);
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setIconPreview(previewUrl);
      }
    };

    const removeCustomIcon = () => {
      setCustomIcon(null);
      if (iconPreview) {
        URL.revokeObjectURL(iconPreview);
        setIconPreview(null);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    // Handle image upload
    const handleImageUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, or WebP)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size must be less than 10MB');
        return;
      }

      setIsUploadingImage(true);
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const response = await api.post('/challenges/upload-image', uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.imageUrl) {
          setFormData(prev => ({ ...prev, image_url: response.data.imageUrl }));
          toast.success('Image uploaded successfully!');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload image: ' + (error.response?.data?.error || error.message));
      } finally {
        setIsUploadingImage(false);
        // Reset file input
        if (imageFileInputRef.current) {
          imageFileInputRef.current.value = '';
        }
      }
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!challenge) return;

      // Validation
      if (!formData.name.trim()) {
        toast.error('Challenge name is required');
        return;
      }
      
      if (!formData.description.trim()) {
        toast.error('Challenge description is required');
        return;
      }
      
      if (!formData.latitude || !formData.longitude) {
        toast.error('Valid latitude and longitude are required');
        return;
      }
      
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      
      if (isNaN(lat) || lat < -90 || lat > 90) {
        toast.error('Latitude must be between -90 and 90 degrees');
        return;
      }
      
      if (isNaN(lng) || lng < -180 || lng > 180) {
        toast.error('Longitude must be between -180 and 180 degrees');
        return;
      }
      
      if (formData.radius < 10 || formData.radius > 1000) {
        toast.error('Radius must be between 10 and 1000 meters');
        return;
      }

      // Build time constraint
      let timeConstraint = null;
      if (tcEnabled) {
        timeConstraint = { timezone: tcTimezone };
        if (tcType === 'window') {
          timeConstraint.type = 'window';
          timeConstraint.start_hour = tcStartHour;
          timeConstraint.end_hour = tcEndHour;
          if (tcDays.length > 0) timeConstraint.days_of_week = tcDays;
        } else if (tcType === 'date_range') {
          timeConstraint.type = 'date_range';
          if (tcDateStart) timeConstraint.start_date = tcDateStart;
          if (tcDateEnd) timeConstraint.end_date = tcDateEnd;
        } else if (tcType === 'combined') {
          timeConstraint.type = 'combined';
          timeConstraint.start_hour = tcStartHour;
          timeConstraint.end_hour = tcEndHour;
          if (tcDays.length > 0) timeConstraint.days_of_week = tcDays;
          if (tcDateStart) timeConstraint.start_date = tcDateStart;
          if (tcDateEnd) timeConstraint.end_date = tcDateEnd;
        }
      }

      // First update the challenge data with translations
      const updatePromise = updateChallengeMutation.mutateAsync({
        id: challenge.id,
        challengeData: {
          ...formData,
          // Round coordinates to 5 decimal places
          latitude: parseFloat(lat.toFixed(5)),
          longitude: parseFloat(lng.toFixed(5)),
          // Include translations
          translations: translations,
          // Include time constraint
          timeConstraint: timeConstraint,
          // Include verification
          verificationType: verifType,
          ...((['qr_code', 'gps_qr'].includes(verifType)) && { qrSecret: verifQrSecret }),
          ...((['passphrase', 'gps_passphrase'].includes(verifType)) && { passphrase: verifPassphrase, passphraseHint: verifPassphraseHint }),
          // Include route config
          challengeMode: editChallengeMode,
          routeType: editChallengeMode === 'multi_point' ? editRouteType : null,
          routeMinRequired: editChallengeMode === 'multi_point' && editRouteType === 'minimum' ? editMinRequired : null,
          // Include category
          category_id: selectedCategoryId || undefined
        }
      });

      // Then upload the icon if there's a custom icon
      const promises = [updatePromise];

      // Save waypoints if this is a route challenge
      if (editChallengeMode === 'multi_point' && editWaypoints.length >= 2) {
        const waypointsPayload = editWaypoints.map((wp, i) => ({
          name: wp.name || `Waypoint ${i + 1}`,
          latitude: parseFloat(wp.latitude),
          longitude: parseFloat(wp.longitude),
          radius: parseInt(wp.radius) || 30,
          isRequired: wp.isRequired !== false
        }));
        promises.push(
          routeChallengesAPI.setWaypoints(challenge.id, waypointsPayload).then(() => {
            // Invalidate cached waypoints
            setRouteWaypointsCache(prev => {
              const next = { ...prev };
              delete next[challenge.id];
              return next;
            });
          })
        );
      }
      if (customIcon) {
        promises.push(
          challengesAPI.uploadIcon(challenge.id, customIcon).then(() => {
            toast.success('Custom icon uploaded successfully!');
          })
        );
      }

      // Execute all promises
      Promise.all(promises).catch((error) => {
        console.error('Update or upload error:', error);
        toast.error('Failed to save all changes');
      });
    };

    if (!isOpen || !challenge) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{t('cms.map.editChallenge')}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Language Tabs - only show if more than one language */}
              {availableLanguages.length > 1 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <label className="label text-xs mb-2">🌐 Language</label>
                  <div className="flex flex-wrap gap-2">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setCurrentLanguage(lang.code)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          currentLanguage === lang.code
                            ? 'bg-secondary-500 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {lang.name}
                        {lang.isDefault && <span className="ml-1 text-xs opacity-75">(default)</span>}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Edit name and description for each language. Other fields are shared across all languages.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    Challenge Name
                    {availableLanguages.length > 1 && (
                      <span className="ml-2 text-xs text-gray-500">({currentLanguage.toUpperCase()})</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={translations[currentLanguage]?.name || formData.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTranslations(prev => ({
                        ...prev,
                        [currentLanguage]: { ...prev[currentLanguage], name: value }
                      }));
                      // Also update formData for default language
                      if (currentLanguage === 'en' || availableLanguages.find(l => l.isDefault)?.code === currentLanguage) {
                        setFormData(prev => ({ ...prev, name: value }));
                      }
                    }}
                    className="input"
                    placeholder="e.g., Brandenburg Gate Visit"
                    required
                  />
                </div>

                <div>
                  <label className="label">Category</label>
                  <div className="relative">
                    <select
                      value={selectedCategoryId}
                      onChange={(e) => {
                        setSelectedCategoryId(e.target.value);
                      }}
                      className="input pl-10"
                    >
                      {categories.length === 0 ? (
                        <option value="">Loading categories...</option>
                      ) : (
                        categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))
                      )}
                    </select>
                    {/* Show selected category icon */}
                    {selectedCategoryId && categories.length > 0 && (
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <LucideIcon
                          name={categories.find(c => c.id === selectedCategoryId)?.icon || 'map-pin'}
                          size={16}
                          color={categories.find(c => c.id === selectedCategoryId)?.color || '#6B7280'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('cms.map.difficulty')}</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="input"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Hard">Hard</option>
                    <option value="Expert">Expert</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Determines challenge complexity and user requirements
                  </p>
                </div>

                <div>
                  <label className="label">Points Reward</label>
                  <input
                    type="number"
                    value={formData.points}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ ...prev, points: value }));
                    }}
                    className="input"
                    min="1"
                    max="1000"
                    step="1"
                    placeholder="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Points earned when user completes this challenge
                  </p>
                </div>
              </div>

              <div>
                <label className="label">
                  Description
                  {availableLanguages.length > 1 && (
                    <span className="ml-2 text-xs text-gray-500">({currentLanguage.toUpperCase()})</span>
                  )}
                </label>
                <textarea
                  value={translations[currentLanguage]?.description || formData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTranslations(prev => ({
                      ...prev,
                      [currentLanguage]: { ...prev[currentLanguage], description: value }
                    }));
                    // Also update formData for default language
                    if (currentLanguage === 'en' || availableLanguages.find(l => l.isDefault)?.code === currentLanguage) {
                      setFormData(prev => ({ ...prev, description: value }));
                    }
                  }}
                  className="input"
                  rows={3}
                  placeholder="Describe the challenge and what users need to do..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('cms.map.radius')}</label>
                  <input
                    type="number"
                    value={formData.radius}
                    onChange={(e) => setFormData(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                    className="input"
                    min="10"
                    max="1000"
                    step="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Users need to be within this distance to complete the challenge
                  </p>
                </div>

                <div>
                  <label className="label">NFT Reward Name</label>
                  <input
                    type="text"
                    value={formData.nftRewardTemplate}
                    onChange={(e) => setFormData(prev => ({ ...prev, nftRewardTemplate: e.target.value }))}
                    className="input"
                    placeholder="e.g., Berlin Explorer: Brandenburg Gate"
                  />
                </div>
              </div>

              {/* Image Selection */}
              <div className="mt-4">
                <label className="label text-xs">
                  Image
                  <span className="text-gray-500 ml-1">(optional)</span>
                </label>
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center space-x-3">
                    {/* Upload Button */}
                    <input
                      type="file"
                      ref={imageFileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => imageFileInputRef.current?.click()}
                      disabled={isUploadingImage}
                      className="btn btn-primary flex items-center space-x-2 disabled:opacity-50"
                    >
                      {isUploadingImage ? (
                        <FiLoader className="w-4 h-4 animate-spin" />
                      ) : (
                        <FiUpload className="w-4 h-4" />
                      )}
                      <span>{isUploadingImage ? 'Uploading...' : 'Upload Image'}</span>
                    </button>
                    {/* Gallery Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setImageGalleryTarget('edit');
                        setShowNFTImageGallery(true);
                      }}
                      className="btn btn-primary"
                    >
                      Choose from Gallery
                    </button>
                  </div>
                  {formData.image_url && (
                    <div className="flex items-start space-x-2">
                      <img
                        src={formData.image_url.startsWith('http') ? formData.image_url : `${(process.env.REACT_APP_API_URL || '').replace('/api', '')}${formData.image_url}`}
                        alt="Selected"
                        className="h-48 w-auto object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: null }))}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <FiX className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* NFT Card Preview */}
              {nftCardImageUrl && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <FiImage className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900">NFT Card Preview</span>
                    <span className="text-xs text-gray-500">(auto-generated)</span>
                  </div>
                  <div className="flex justify-center">
                    <img
                      src={nftCardImageUrl.startsWith('http') ? nftCardImageUrl : `${(process.env.REACT_APP_API_URL || '').replace('/api', '')}${nftCardImageUrl}`}
                      alt="NFT Card"
                      className="h-64 w-auto object-contain rounded-lg shadow-md"
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    NFT card regenerates when challenge image or title changes
                  </p>
                </div>
              )}

              {/* Time Restrictions */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowTcSection(!showTcSection)}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <FiClock className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">Time Restrictions</span>
                    {tcEnabled && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Active</span>}
                  </div>
                  {showTcSection ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {showTcSection && (
                  <div className="p-3 pt-0 space-y-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" checked={tcEnabled} onChange={(e) => setTcEnabled(e.target.checked)} className="w-4 h-4 text-purple-600 rounded" />
                      <span className="text-sm text-gray-700">Enable time restrictions</span>
                    </label>
                    {tcEnabled && (
                      <>
                        <select value={tcType} onChange={(e) => setTcType(e.target.value)} className="input text-sm">
                          <option value="window">Time Window</option>
                          <option value="date_range">Date Range</option>
                          <option value="combined">Both</option>
                        </select>
                        {(tcType === 'window' || tcType === 'combined') && (
                          <div className="p-2 bg-purple-50 rounded space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Start</label>
                                <select value={tcStartHour} onChange={(e) => setTcStartHour(parseInt(e.target.value))} className="input text-sm">
                                  {Array.from({length:24},(_,i)=><option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">End</label>
                                <select value={tcEndHour} onChange={(e) => setTcEndHour(parseInt(e.target.value))} className="input text-sm">
                                  {Array.from({length:24},(_,i)=><option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {[{v:0,l:'Su'},{v:1,l:'Mo'},{v:2,l:'Tu'},{v:3,l:'We'},{v:4,l:'Th'},{v:5,l:'Fr'},{v:6,l:'Sa'}].map(d=>(
                                <button key={d.v} type="button" onClick={()=>setTcDays(p=>p.includes(d.v)?p.filter(x=>x!==d.v):[...p,d.v].sort())}
                                  className={`px-2 py-1 rounded text-xs font-medium ${tcDays.includes(d.v)?'bg-purple-600 text-white':'bg-white text-gray-600 border border-gray-300'}`}>{d.l}</button>
                              ))}
                            </div>
                          </div>
                        )}
                        {(tcType === 'date_range' || tcType === 'combined') && (
                          <div className="grid grid-cols-2 gap-2 p-2 bg-blue-50 rounded">
                            <div><label className="block text-xs text-gray-600 mb-1">Start Date</label><input type="date" value={tcDateStart} onChange={e=>setTcDateStart(e.target.value)} className="input text-sm" /></div>
                            <div><label className="block text-xs text-gray-600 mb-1">End Date</label><input type="date" value={tcDateEnd} onChange={e=>setTcDateEnd(e.target.value)} className="input text-sm" /></div>
                          </div>
                        )}
                        <select value={tcTimezone} onChange={e=>setTcTimezone(e.target.value)} className="input text-sm">
                          <option value="Europe/Berlin">Europe/Berlin</option>
                          <option value="Europe/Vienna">Europe/Vienna</option>
                          <option value="Europe/Zurich">Europe/Zurich</option>
                          <option value="Europe/London">Europe/London</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Route Challenge Mode */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => {/* toggle handled inline */}}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <FiNavigation className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900">Challenge Mode</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      editChallengeMode === 'multi_point' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {editChallengeMode === 'multi_point'
                        ? `Route - ${editRouteType === 'ordered' ? 'Ordered' : editRouteType === 'unordered' ? 'Collect All' : `Min ${editMinRequired}`}`
                        : 'Single Point'}
                    </span>
                  </div>
                </button>
                <div className="p-3 pt-0 space-y-3">
                  {/* Mode Toggle */}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEditChallengeMode('single_point')}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${editChallengeMode === 'single_point' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      Single Point
                    </button>
                    <button type="button" onClick={() => setEditChallengeMode('multi_point')}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${editChallengeMode === 'multi_point' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      Route
                    </button>
                  </div>

                  {editChallengeMode === 'multi_point' && (
                    <>
                      {/* Route Type */}
                      <div className="flex gap-1.5">
                        {[
                          { value: 'ordered', label: 'Ordered' },
                          { value: 'unordered', label: 'Collect All' },
                          { value: 'minimum', label: 'Minimum X' }
                        ].map(opt => (
                          <button key={opt.value} type="button" onClick={() => setEditRouteType(opt.value)}
                            className={`flex-1 px-2 py-1 rounded-lg text-xs font-medium transition-all ${editRouteType === opt.value ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {editRouteType === 'minimum' && (
                        <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg">
                          <label className="text-xs font-medium text-indigo-800">Min required:</label>
                          <input type="number" value={editMinRequired} onChange={(e) => setEditMinRequired(parseInt(e.target.value) || 1)}
                            min="1" max={editWaypoints.length} className="w-16 px-2 py-1 border border-indigo-200 rounded text-xs" />
                          <span className="text-xs text-indigo-600">of {editWaypoints.length}</span>
                        </div>
                      )}

                      {/* Waypoints list */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('cms.map.waypoints')} ({editWaypoints.length})</span>
                          <button type="button" onClick={() => setEditWaypoints(prev => [...prev, { name: `Waypoint ${prev.length + 1}`, latitude: '', longitude: '', radius: 30, isRequired: true }])}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded">
                            <FiPlus className="w-3 h-3" /> {t('cms.map.addWaypoint')}
                          </button>
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1.5">
                          {editWaypoints.map((wp, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                              <input type="text" value={wp.name} onChange={(e) => { const v = e.target.value; setEditWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, name: v } : w)); }}
                                placeholder="Name" className="w-20 px-1.5 py-0.5 border border-gray-200 rounded text-xs" />
                              <input type="text" value={wp.latitude} onChange={(e) => { const v = e.target.value; setEditWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, latitude: v } : w)); }}
                                placeholder="Lat" className="w-16 px-1.5 py-0.5 border border-gray-200 rounded text-xs font-mono" />
                              <input type="text" value={wp.longitude} onChange={(e) => { const v = e.target.value; setEditWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, longitude: v } : w)); }}
                                placeholder="Lng" className="w-16 px-1.5 py-0.5 border border-gray-200 rounded text-xs font-mono" />
                              <input type="number" value={wp.radius} onChange={(e) => { const v = parseInt(e.target.value) || 30; setEditWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, radius: v } : w)); }}
                                min="10" max="500" className="w-12 px-1 py-0.5 border border-gray-200 rounded text-xs" title="Radius" />
                              <div className="flex items-center gap-0.5">
                                <button type="button" onClick={() => { if (idx === 0) return; const nw = [...editWaypoints]; [nw[idx], nw[idx-1]] = [nw[idx-1], nw[idx]]; setEditWaypoints(nw); }}
                                  disabled={idx === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"><FiArrowUp className="w-3 h-3" /></button>
                                <button type="button" onClick={() => { if (idx === editWaypoints.length - 1) return; const nw = [...editWaypoints]; [nw[idx], nw[idx+1]] = [nw[idx+1], nw[idx]]; setEditWaypoints(nw); }}
                                  disabled={idx === editWaypoints.length - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"><FiArrowDown className="w-3 h-3" /></button>
                                <button type="button" onClick={() => setEditWaypoints(prev => prev.filter((_, i) => i !== idx))}
                                  className="p-0.5 text-red-400 hover:text-red-600"><FiTrash2 className="w-3 h-3" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Pick from map button */}
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            setIsPickingWaypoints(true);
                            setPickedWaypoints([]);
                            setWaypointPickCallback(() => (points) => {
                              const newWaypoints = points.map((p, i) => ({
                                name: `Waypoint ${i + 1}`,
                                latitude: String(p.lat),
                                longitude: String(p.lng),
                                radius: 30,
                                isRequired: true
                              }));
                              setEditWaypoints(newWaypoints);
                              setEditChallengeMode('multi_point');
                              // Re-open edit modal
                              setShowEditModal(true);
                            });
                          }}
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                        >
                          <FiMapPin className="w-3 h-3" /> {t('cms.map.selectOnMap')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Verification Method */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowVerifSection(!showVerifSection)}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-2">
                    <FiGrid className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-900">Verification Method</span>
                    {verifType !== 'gps' && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{verifType.replace('_', ' ').replace('gps ', 'GPS+')}</span>}
                  </div>
                  {showVerifSection ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {showVerifSection && (
                  <div className="p-3 pt-0 space-y-3">
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'gps', label: 'GPS Only', color: 'blue' },
                        { value: 'qr_code', label: 'QR Code', color: 'green' },
                        { value: 'passphrase', label: 'Passphrase', color: 'amber' },
                        { value: 'gps_qr', label: 'GPS+QR', color: 'green' },
                        { value: 'gps_passphrase', label: 'GPS+Pass', color: 'amber' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setVerifType(opt.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            verifType === opt.value
                              ? `bg-${opt.color}-500 text-white shadow-sm`
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          style={verifType === opt.value ? {
                            backgroundColor: opt.color === 'green' ? '#22c55e' : opt.color === 'amber' ? '#f59e0b' : '#3b82f6',
                            color: 'white'
                          } : {}}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {['qr_code', 'gps_qr'].includes(verifType) && (
                      <div className="p-3 bg-green-50 rounded-lg space-y-2">
                        <label className="block text-xs font-medium text-gray-700">QR Secret</label>
                        <div className="flex gap-2">
                          <input type="text" value={verifQrSecret} readOnly className="input text-sm flex-1 bg-white font-mono text-xs" />
                          <button type="button" onClick={() => setVerifQrSecret(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 12))}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors" title="Regenerate">
                            <FiRefreshCw className="w-4 h-4" />
                          </button>
                          {verifQrDataUrl && (
                            <a href={verifQrDataUrl} download={`qr-${challenge?.id || 'challenge'}.png`}
                              className="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors" title="Download QR">
                              <FiDownload className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        {verifQrDataUrl && (
                          <div className="flex justify-center mt-2">
                            <img src={verifQrDataUrl} alt="QR Code" className="w-32 h-32 border border-green-200 rounded" />
                          </div>
                        )}
                      </div>
                    )}

                    {['passphrase', 'gps_passphrase'].includes(verifType) && (
                      <div className="p-3 bg-amber-50 rounded-lg space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Passphrase</label>
                          <input type="text" value={verifPassphrase} onChange={e => setVerifPassphrase(e.target.value)}
                            placeholder="Enter the secret passphrase..." className="input text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Hint (optional)</label>
                          <input type="text" value={verifPassphraseHint} onChange={e => setVerifPassphraseHint(e.target.value)}
                            placeholder="A hint shown to users..." className="input text-sm" />
                        </div>
                      </div>
                    )}

                    {['qr_code', 'passphrase'].includes(verifType) && (
                      <p className="text-xs text-gray-500 italic">GPS verification disabled - location shown on map but not required for completion.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">{t('cms.map.coordinates')}</h4>
                  <button
                    type="button"
                    onClick={() => {
                      // Store the challenge being repositioned
                      setRepositioningChallenge(challenge);
                      // Set repositioning mode FIRST, before closing modal
                      setIsRepositioningMarker(true);
                      setTempMarkerLocation(null);
                      setShowLocationConfirm(false);
                      // Pan map to challenge location so user can see the area
                      const challengeLat = parseFloat(formData.latitude);
                      const challengeLng = parseFloat(formData.longitude);
                      if (mapRef.current && !isNaN(challengeLat) && !isNaN(challengeLng)) {
                        mapRef.current.setView([challengeLat, challengeLng], 16);
                      }
                      // Close the modal after setting the flag
                      setTimeout(() => {
                        onClose();
                      }, 0);
                      toast('Click on the map to select new location', {
                        icon: '📍',
                        duration: 4000
                      });
                    }}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center space-x-2 transition-colors ${
                      isRepositioningMarker 
                        ? 'bg-secondary-600 text-white' 
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FiMapPin className="w-4 h-4" />
                    <span>{isRepositioningMarker ? 'Selecting Location...' : 'Move Marker'}</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Latitude</label>
                    <input
                      type="number"
                      value={formData.latitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                      className="input"
                      step="0.000001"
                      min="-90"
                      max="90"
                      placeholder="e.g., 52.520007"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Decimal degrees (-90 to 90)
                    </p>
                  </div>
                  <div>
                    <label className="label">Longitude</label>
                    <input
                      type="number"
                      value={formData.longitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                      className="input"
                      step="0.000001"
                      min="-180"
                      max="180"
                      placeholder="e.g., 13.404954"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Decimal degrees (-180 to 180)
                    </p>
                  </div>
                </div>
              </div>

              {/* Custom Icon Upload Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="label mb-3 flex items-center space-x-2">
                  <span>Custom Icon</span>
                  <span className="text-xs text-gray-500">(Optional - overrides group icon)</span>
                </label>

                <div className="flex items-start space-x-4">
                  {/* Current Icon Preview */}
                  <div className="flex flex-col items-center space-y-2">
                    <div className="text-xs text-gray-500 font-medium">Current Icon</div>
                    <div className="w-16 h-16 rounded-lg border-2 border-gray-200 flex items-center justify-center bg-white">
                      {iconPreview ? (
                        <img
                          src={iconPreview}
                          alt="Custom icon preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <img
                          src={challengeTypes[formData.type]?.svgPath}
                          alt={formData.type}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      )}
                      <div
                        className="w-full h-full hidden items-center justify-center text-2xl rounded-lg"
                        style={{ backgroundColor: challengeTypes[formData.type]?.color, color: 'white' }}
                      >
                        {renderTypeIcon(formData.type, 32)}
                      </div>
                    </div>
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1 space-y-3">
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary flex items-center space-x-2"
                      >
                        <FiPlus className="w-4 h-4" />
                        <span>Upload Icon</span>
                      </button>

                      {(customIcon || iconPreview) && (
                        <button
                          type="button"
                          onClick={removeCustomIcon}
                          className="btn btn-outline-danger flex items-center space-x-2"
                        >
                          <FiX className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg"
                      onChange={handleIconUpload}
                      className="hidden"
                    />

                    <div className="text-xs text-gray-500">
                      <p>• Supported formats: SVG, PNG, JPG</p>
                      <p>• Max file size: 1MB</p>
                      <p>• Recommended: 32x32px SVG for best quality</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ratings Section (read-only) */}
              {(challenge?.totalRatings > 0 || challenge?.averageRating) && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Ratings</h4>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(star => (
                        <span key={star} className={`text-lg ${star <= Math.round(challenge?.averageRating || 0) ? 'text-amber-400' : 'text-gray-300'}`}>★</span>
                      ))}
                    </div>
                    <span className="text-lg font-semibold text-gray-800">{challenge?.averageRating?.toFixed(1) || '0.0'}</span>
                    <span className="text-sm text-gray-500">({challenge?.totalRatings || 0} ratings)</span>
                  </div>
                  <RatingsBreakdown challengeId={challenge?.id} />
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button type="button" onClick={onClose} className="btn btn-secondary">
                  {t('cms.common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updateChallengeMutation.isLoading}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  {updateChallengeMutation.isLoading ? (
                    <FiLoader className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiSave className="w-4 h-4" />
                  )}
                  <span>{t('cms.common.save')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal Component
  const DeleteConfirmationModal = ({ isOpen, onClose, challenge }) => {
    // ESC key handler
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onClose]);

    if (!isOpen || !challenge) return null;

    const handleDelete = () => {
      deleteChallengeMutation.mutate(challenge.id);
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiTrash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t('cms.map.deleteChallenge')}</h3>
                <p className="text-sm text-gray-500">The challenge will be hidden but can be restored later.</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>"{challenge.name}"</strong>?
                The challenge will be hidden from users and the CMS by default. You can restore it later using the "Show Deleted" filter.
              </p>
              
              {challenge.stats?.completions > 0 && (
                <div className="mt-3 p-3 bg-orange-50 rounded-md">
                  <p className="text-orange-700 text-sm flex items-start gap-2">
                    <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>This challenge has <strong>{challenge.stats.completions} completions</strong>.
                    Deleting it may affect user achievements.</span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteChallengeMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {deleteChallengeMutation.isLoading ? (
                  <FiLoader className="w-4 h-4 animate-spin" />
                ) : (
                  <FiTrash2 className="w-4 h-4" />
                )}
                <span>{t('cms.map.deleteChallenge')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Permanent Delete Confirmation Modal Component
  const PermanentDeleteConfirmationModal = ({ isOpen, onClose, challenge }) => {
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onClose]);

    if (!isOpen || !challenge) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiTrash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Permanently Delete Challenge</h3>
                <p className="text-sm text-red-600 font-medium">This action cannot be undone.</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to permanently remove <strong>"{challenge.name}"</strong> from the database?
              </p>
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-700 text-sm flex items-start gap-2">
                  <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>This will permanently remove the challenge and cannot be undone. User achievements and their images will be preserved.</span>
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={() => permanentDeleteMutation.mutate(challenge.id)}
                disabled={permanentDeleteMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {permanentDeleteMutation.isLoading ? (
                  <FiLoader className="w-4 h-4 animate-spin" />
                ) : (
                  <FiTrash2 className="w-4 h-4" />
                )}
                <span>{t('cms.common.delete')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.map')}</h1>
          <p className="text-gray-600">{t('cms.map.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Challenge Generator Button */}
          <button
            onClick={handleOpenRefinement}
            disabled={generating}
            className="btn btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            <FiZap className="w-4 h-4" />
            <span>{generating ? t('cms.map.generating') : t('cms.map.aiGenerate')}</span>
          </button>

          {/* Location Search */}
          <div className="relative" ref={searchInputRef}>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cms.common.search')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.trim() && !showSearchDropdown) {
                    setShowSearchDropdown(true);
                  }
                }}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchDropdown(true);
                  }
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {isSearching && (
                <FiLoader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    onClick={() => handleLocationSelect(result)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FiMapPin className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {result.address.street && result.address.house_number 
                            ? `${result.address.street} ${result.address.house_number}`
                            : result.display_name.split(',')[0]
                          }
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          {result.address.city && result.address.state
                            ? `${result.address.city}, ${result.address.state}, ${result.address.country}`
                            : result.display_name.split(',').slice(1, 3).join(',').trim()
                          }
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {result.lat.toFixed(5)}, {result.lon.toFixed(5)} • {result.type}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {userLocation && (
            <button
              onClick={jumpToUserLocation}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <FiTarget className="w-4 h-4" />
              <span>{t('cms.map.location')}</span>
            </button>
          )}
          <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            <FiMapPin className="w-4 h-4" />
            <span>Double click map to add challenge</span>
          </div>
        </div>
      </div>

      {/* Compact Filters Bar */}
      <div className="card mb-4">
        <div className="space-y-3">
          {/* Always visible: Category + Search + Results + Expand */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Filter (replaces old Type filter) */}
            <span className="text-sm font-medium text-gray-600">Category:</span>
            <div className="flex flex-wrap gap-1.5">
              {categories.map(category => {
                const isSelected = filterCategories.includes(category.slug);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setFilterCategories(filterCategories.filter(c => c !== category.slug));
                      } else {
                        setFilterCategories([...filterCategories, category.slug]);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? 'text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    style={isSelected ? { backgroundColor: category.color } : {}}
                    title={category.name}
                  >
                    <LucideIcon name={category.icon} size={14} />
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="w-px h-6 bg-gray-200 hidden md:block"></div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">{t('cms.common.search')}:</span>
              <div className="relative">
                <FiSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  placeholder="Name or description..."
                  className="w-48 pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex-1"></div>

            {/* Results Count */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700">{filteredChallenges.length}</span> / {challengesData?.challenges?.length || 0}
              </span>
              {bulkSelectMode && (
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  {selectedChallengeIds.size === selectableChallenges.length ? <FiCheckSquare className="w-4 h-4" /> : <FiSquare className="w-4 h-4" />}
                  {selectedChallengeIds.size === selectableChallenges.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
              {(filterCategories.length > 0 || filterLocation || filterDifficulties.length > 0 || filterPointsMin || filterPointsMax || filterTimeRestricted !== null || filterActiveStatus !== null || filterRated !== null || filterVerification !== null || filterRouteMode !== null || filterHiddenGems || sortBy !== 'newest' || showDeleted) && (
                <button
                  onClick={() => {
                    setFilterCategories([]);
                    setFilterLocation('');
                    setFilterDifficulties([]);
                    setFilterPointsMin('');
                    setFilterPointsMax('');
                    setFilterTimeRestricted(null);
                    setFilterActiveStatus(null);
                    setFilterRated(null);
                    setFilterVerification(null);
                    setFilterRouteMode(null);
                    setFilterHiddenGems(false);
                    setSortBy('newest');
                    setShowDeleted(false);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  <FiX className="w-4 h-4" />
                  {t('cms.map.clearSelection')}
                </button>
              )}
              <button
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  showMoreFilters ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FiSettings className="w-3.5 h-3.5" />
                <span>{t('cms.common.filter')}</span>
                {(filterDifficulties.length > 0 || filterPointsMin || filterPointsMax || filterTimeRestricted !== null || filterActiveStatus !== null || filterRated !== null || filterVerification !== null || filterRouteMode !== null || filterHiddenGems || sortBy !== 'newest' || showDeleted) && (
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                )}
                {showMoreFilters ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Expandable filters: 3-column grid */}
          {showMoreFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-gray-200">
              {/* Column 1: Difficulty + Points */}
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Difficulty</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[
                      { name: 'Easy', color: 'green' },
                      { name: 'Moderate', color: 'yellow' },
                      { name: 'Hard', color: 'orange' },
                      { name: 'Expert', color: 'red' }
                    ].map(({ name: difficulty, color }) => {
                      const isSelected = filterDifficulties.includes(difficulty);
                      const colorClasses = {
                        green: 'bg-green-500 text-white',
                        yellow: 'bg-yellow-500 text-white',
                        orange: 'bg-orange-500 text-white',
                        red: 'bg-red-500 text-white'
                      };
                      return (
                        <button
                          key={difficulty}
                          onClick={() => {
                            if (isSelected) {
                              setFilterDifficulties(filterDifficulties.filter(d => d !== difficulty));
                            } else {
                              setFilterDifficulties([...filterDifficulties, difficulty]);
                            }
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? colorClasses[color] + ' shadow-sm'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {difficulty}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Points Range</span>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input
                      type="number"
                      value={filterPointsMin}
                      onChange={(e) => setFilterPointsMin(e.target.value)}
                      placeholder="Min"
                      className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="number"
                      value={filterPointsMax}
                      onChange={(e) => setFilterPointsMax(e.target.value)}
                      placeholder="Max"
                      className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Column 2: Time + Status + Verification */}
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</span>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      onClick={() => setFilterTimeRestricted(prev => prev === true ? null : true)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterTimeRestricted === true ? 'bg-purple-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiClock className="w-3 h-3" /> Timed
                    </button>
                    <button
                      onClick={() => setFilterTimeRestricted(prev => prev === false ? null : false)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        filterTimeRestricted === false ? 'bg-gray-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Anytime
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      onClick={() => setFilterActiveStatus(prev => prev === true ? null : true)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterActiveStatus === true ? 'bg-green-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiEye className="w-3 h-3" /> Active
                    </button>
                    <button
                      onClick={() => setFilterActiveStatus(prev => prev === false ? null : false)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterActiveStatus === false ? 'bg-gray-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiEyeOff className="w-3 h-3" /> Inactive
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode</span>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      onClick={() => setFilterRouteMode(prev => prev === 'route' ? null : 'route')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterRouteMode === 'route' ? 'bg-indigo-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiNavigation className="w-3 h-3" /> Route
                    </button>
                    <button
                      onClick={() => setFilterRouteMode(prev => prev === 'single' ? null : 'single')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterRouteMode === 'single' ? 'bg-gray-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiMapPin className="w-3 h-3" /> Single
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verification</span>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      onClick={() => setFilterVerification(prev => prev === 'qr' ? null : 'qr')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterVerification === 'qr' ? 'bg-green-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiGrid className="w-3 h-3" /> QR
                    </button>
                    <button
                      onClick={() => setFilterVerification(prev => prev === 'passphrase' ? null : 'passphrase')}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterVerification === 'passphrase' ? 'bg-amber-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiKey className="w-3 h-3" /> Pass
                    </button>
                  </div>
                </div>
              </div>

              {/* Column 3: Rating + Sort + Deleted */}
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rating</span>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      onClick={() => setFilterRated(prev => prev === true ? null : true)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                        filterRated === true ? 'bg-amber-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FiStar className="w-3 h-3" /> Rated
                    </button>
                    <button
                      onClick={() => setFilterRated(prev => prev === false ? null : false)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        filterRated === false ? 'bg-gray-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Unrated
                    </button>
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort By</span>
                  <div className="mt-1.5">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 border-0 focus:ring-2 focus:ring-primary-500 cursor-pointer"
                    >
                      <option value="newest">Newest</option>
                      <option value="name">Name A-Z</option>
                      <option value="points">Points</option>
                      <option value="completions">Completions</option>
                      <option value="rating">Rating</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={filterHiddenGems}
                      onChange={(e) => setFilterHiddenGems(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="text-xs text-gray-600">💎 Show only Hidden Gems</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />
                    <FiTrash2 className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">Show Deleted</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map and Challenges Section */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Challenges List Sidebar */}
        <div className="xl:col-span-1 order-2 xl:order-1">
          <div className="card h-full">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">Challenges in View</h3>
                <button
                  onClick={() => {
                    setBulkSelectMode(!bulkSelectMode);
                    if (bulkSelectMode) setSelectedChallengeIds(new Set());
                  }}
                  className={`px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 transition-colors ${bulkSelectMode ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <FiCheckSquare className="w-3 h-3" />
                  <span>{bulkSelectMode ? t('cms.common.cancel') : t('cms.map.selected')}</span>
                </button>
                {bulkSelectMode && (
                  <button
                    onClick={toggleSelectAll}
                    className="px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    {selectedChallengeIds.size === selectableChallenges.length ? <FiCheckSquare className="w-3 h-3" /> : <FiSquare className="w-3 h-3" />}
                    <span>{selectedChallengeIds.size === selectableChallenges.length ? 'Deselect All' : 'Select All'}</span>
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FiMapPin className="w-4 h-4" />
                <span>{challengesData?.challenges?.length || 0}</span>
              </div>
            </div>
            {/* Bulk Actions - shown when items are selected */}
            {bulkSelectMode && selectedChallengeIds.size > 0 && (
              <div className="flex items-center space-x-2 mb-3">
                <button
                  onClick={() => handleBulkToggleActive(true)}
                  className="px-2 py-1 rounded text-xs font-medium bg-green-500 text-white hover:bg-green-600 flex items-center space-x-1"
                >
                  <FiEye className="w-3 h-3" />
                  <span>{t('cms.map.activate')} ({selectedChallengeIds.size})</span>
                </button>
                <button
                  onClick={() => handleBulkToggleActive(false)}
                  className="px-2 py-1 rounded text-xs font-medium bg-gray-500 text-white hover:bg-gray-600 flex items-center space-x-1"
                >
                  <FiEyeOff className="w-3 h-3" />
                  <span>{t('cms.map.deactivate')} ({selectedChallengeIds.size})</span>
                </button>
              </div>
            )}
            
            {challengesLoading ? (
              <div className="flex items-center justify-center py-8">
                <FiLoader className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredChallenges.length > 0 ? (
                  filteredChallenges.map((challenge) => (
                    <div
                      key={challenge.id}
                      className={`p-3 rounded-lg border transition-all hover:shadow-md ${
                        challenge.isDeleted
                          ? 'border-red-200 bg-red-50 opacity-50'
                          : selectedChallenge?.id === challenge.id
                            ? 'border-primary-500 bg-primary-50'
                            : challenge.isActive === false
                              ? 'border-gray-300 bg-gray-50 opacity-60'
                              : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Bulk selection checkbox (skip deleted) */}
                        {bulkSelectMode && !challenge.isDeleted && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleChallengeSelection(challenge.id);
                            }}
                            className="flex-shrink-0 mt-1"
                          >
                            {selectedChallengeIds.has(challenge.id)
                              ? <FiCheckSquare className="w-5 h-5 text-primary-600" />
                              : <FiSquare className="w-5 h-5 text-gray-400" />
                            }
                          </button>
                        )}
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0 cursor-pointer"
                          style={{ backgroundColor: challengeTypes[challenge.type]?.color }}
                          onClick={() => {
                            setSelectedChallenge(challenge);
                            if (mapRef.current) {
                              mapRef.current.setView([challenge.location.latitude, challenge.location.longitude], 15);
                            }
                          }}
                        >
                          {renderChallengeIcon(challenge, 20)}
                        </div>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => {
                            if (bulkSelectMode) {
                              toggleChallengeSelection(challenge.id);
                              return;
                            }
                            setSelectedChallenge(challenge);
                            if (mapRef.current) {
                              mapRef.current.setView([challenge.location.latitude, challenge.location.longitude], 15);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <h4 className={`font-medium truncate ${challenge.isDeleted ? 'text-red-400 line-through' : 'text-gray-900'}`}>{challenge.name}</h4>
                            {challenge.isDeleted && (
                              <span className="text-xs px-1.5 py-0.5 bg-red-200 text-red-700 rounded font-medium flex-shrink-0">Deleted</span>
                            )}
                            {!challenge.isDeleted && challenge.isActive === false && (
                              <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded font-medium flex-shrink-0">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{challenge.description}</p>
                          <div className="flex items-center flex-wrap gap-2 mt-2 text-xs text-gray-500">
                            <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: `${challengeTypes[challenge.type]?.color}20`, color: challengeTypes[challenge.type]?.color }}>{challengeTypes[challenge.type]?.name}</span>
                            <span className={`px-1.5 py-0.5 rounded ${
                              challenge.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                              challenge.difficulty === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                              challenge.difficulty === 'Hard' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>{challenge.difficulty}</span>
                            <span className="font-medium">{challenge.points} pts</span>
                            <span>{challenge.stats.completions} done</span>
                            {challenge.totalRatings > 0 && (
                              <span className="text-amber-600 font-medium">★ {challenge.averageRating?.toFixed(1)} ({challenge.totalRatings})</span>
                            )}
                            {challenge.hiddenGem && (
                              <span className="text-pink-600 font-medium">💎 Gem</span>
                            )}
                            {challenge.challengeMode === 'multi_point' && (
                              <span className="text-indigo-600 font-medium flex items-center gap-0.5">
                                <FiNavigation className="w-3 h-3" />
                                {challenge.routeType === 'ordered' ? 'Ordered' :
                                 challenge.routeType === 'unordered' ? 'Collect All' :
                                 challenge.routeType === 'minimum' ? `Min ${challenge.routeMinRequired}` : 'Route'}
                              </span>
                            )}
                          </div>
                          {challenge.timeConstraint && (
                            <div className="flex items-center mt-1 text-xs text-purple-600">
                              <FiClock className="w-3 h-3 mr-1" />
                              <span>
                                {challenge.timeConstraint.type === 'window' && `${challenge.timeConstraint.start_hour}:00-${challenge.timeConstraint.end_hour}:00`}
                                {challenge.timeConstraint.type === 'date_range' && `${challenge.timeConstraint.start_date ? new Date(challenge.timeConstraint.start_date).toLocaleDateString() : '...'} - ${challenge.timeConstraint.end_date ? new Date(challenge.timeConstraint.end_date).toLocaleDateString() : '...'}`}
                                {challenge.timeConstraint.type === 'combined' && `${challenge.timeConstraint.start_hour}:00-${challenge.timeConstraint.end_hour}:00`}
                              </span>
                            </div>
                          )}
                          {challenge.verificationType && challenge.verificationType !== 'gps' && (
                            <div className="flex items-center mt-1 text-xs gap-1">
                              {['qr_code', 'gps_qr'].includes(challenge.verificationType) && (
                                <span className="flex items-center text-green-600"><FiGrid className="w-3 h-3 mr-0.5" />QR</span>
                              )}
                              {['passphrase', 'gps_passphrase'].includes(challenge.verificationType) && (
                                <span className="flex items-center text-amber-600"><FiKey className="w-3 h-3 mr-0.5" />Passphrase</span>
                              )}
                              {!['qr_code', 'gps_qr', 'passphrase', 'gps_passphrase'].includes(challenge.verificationType) || ['gps_qr', 'gps_passphrase'].includes(challenge.verificationType) ? null : null}
                              {['gps_qr', 'gps_passphrase'].includes(challenge.verificationType) && (
                                <span className="text-blue-500">+ GPS</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col space-y-1">
                          {challenge.isDeleted ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  restoreChallengeMutation.mutate(challenge.id);
                                }}
                                disabled={restoreChallengeMutation.isLoading}
                                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                title="Restore challenge"
                              >
                                <FiRotateCcw className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermanentDeletingChallenge(challenge);
                                  setShowPermanentDeleteModal(true);
                                }}
                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Permanently delete"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingChallenge(challenge);
                                  setShowEditModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                title="Edit challenge"
                              >
                                <FiEdit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cloneChallengeMutation.mutate(challenge.id);
                                }}
                                disabled={cloneChallengeMutation.isLoading}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Duplicate challenge"
                              >
                                <FiCopy className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingChallenge(challenge);
                                  setShowDeleteModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete challenge"
                              >
                                <FiTrash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FiMapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>{t('cms.map.noResults')}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Map Info */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center justify-between">
                  <span>Zoom Level:</span>
                  <span className="font-medium">{mapZoom}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>GPS Status:</span>
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${userLocation ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className="text-xs">{userLocation ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map Controls */}
        <div className="xl:col-span-3 order-1 xl:order-2">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <FiMapPin className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">Interactive Map</span>
                </div>
                {showCreateModal && (
                  <div className="flex items-center space-x-2 text-sm text-primary-600">
                    <div className="w-2 h-2 bg-secondary-600 rounded-full animate-pulse"></div>
                    <span>Click map to place challenge location</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                {showCreateModal && (
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-danger flex items-center space-x-2"
                  >
                    <FiX className="w-4 h-4" />
                    <span>{t('cms.common.cancel')}</span>
                  </button>
                )}
                
                {/* Create mode instructions */}
                {showCreateModal && (
                  <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 animate-pulse"></div>
                      <div className="text-sm text-primary-800">
                        <p className="font-medium">Create Mode Active</p>
                        <p className="text-primary-600 mt-1">
                          Click anywhere on the map to place a new challenge, or double-click for quick placement.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Map Container */}
            <div className="h-96 lg:h-[600px] rounded-lg overflow-hidden border border-gray-200 relative z-0">
              {/* Repositioning Mode Overlay */}
              {isRepositioningMarker && !showLocationConfirm && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-secondary-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2">
                  <FiMapPin className="w-5 h-5 animate-bounce" />
                  <span className="font-medium">Click on the map to set new location</span>
                  <button
                    onClick={() => {
                      setIsRepositioningMarker(false);
                      setTempMarkerLocation(null);
                      setRepositioningChallenge(null);
                      toast.info('Location selection cancelled');
                    }}
                    className="ml-2 hover:bg-secondary-700 rounded p-1"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Waypoint Pick Mode Overlay */}
              {isPickingWaypoints && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center gap-2" style={{ zIndex: 1000 }}>
                  <div className="bg-indigo-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center space-x-3 border-2 border-indigo-300">
                    <FiNavigation className="w-5 h-5 animate-pulse flex-shrink-0" />
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">Click map to place waypoints</span>
                      <span className="text-indigo-200 text-xs">
                        {pickedWaypoints.length < 2
                          ? `Need at least 2 points (${pickedWaypoints.length} placed)`
                          : `${pickedWaypoints.length} points placed — ready!`}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 pl-3 border-l border-indigo-400">
                      <button
                        onClick={() => {
                          if (pickedWaypoints.length > 0) {
                            setPickedWaypoints(prev => prev.slice(0, -1));
                          }
                        }}
                        disabled={pickedWaypoints.length === 0}
                        className="hover:bg-indigo-700 rounded px-2 py-1 text-xs disabled:opacity-40"
                      >
                        Undo
                      </button>
                      <button
                        onClick={() => {
                          if (waypointPickCallback && pickedWaypoints.length >= 2) {
                            waypointPickCallback(pickedWaypoints);
                          }
                          setIsPickingWaypoints(false);
                          setPickedWaypoints([]);
                          setWaypointPickCallback(null);
                        }}
                        disabled={pickedWaypoints.length < 2}
                        className={`font-semibold rounded-lg px-4 py-1.5 text-sm transition-all ${
                          pickedWaypoints.length >= 2
                            ? 'bg-white text-indigo-700 hover:bg-indigo-50 shadow-md ring-2 ring-white/50'
                            : 'bg-indigo-500 text-indigo-300 opacity-40 cursor-not-allowed'
                        }`}
                      >
                        Done ({pickedWaypoints.length} pts)
                      </button>
                      <button
                        onClick={() => {
                          setIsPickingWaypoints(false);
                          setPickedWaypoints([]);
                          setWaypointPickCallback(null);
                        }}
                        className="hover:bg-indigo-700 rounded p-1 text-indigo-200 hover:text-white"
                        title="Cancel"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full cursor-grab"
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapEventHandler />

            {/* User Location */}
            {userLocation && (
              <>
                <Marker position={userLocation}>
                  <Popup>
                    <div className="text-center">
                      <div className="text-lg mb-1">📍</div>
                      <div className="font-medium">Your Location</div>
                      <div className="text-sm text-gray-600">
                        Accuracy: ±{Math.round(locationAccuracy)}m
                      </div>
                    </div>
                  </Popup>
                </Marker>
                {locationAccuracy && (
                  <Circle
                    center={userLocation}
                    radius={locationAccuracy}
                    pathOptions={{
                      fillColor: 'blue',
                      fillOpacity: 0.1,
                      color: 'blue',
                      weight: 1
                    }}
                  />
                )}
              </>
            )}

            {/* Existing Challenges */}
            {filteredChallenges.map((challenge) => (
              <React.Fragment key={challenge.id}>
                <Marker
                  position={[challenge.location.latitude, challenge.location.longitude]}
                  icon={createCustomIcon(challenge)}
                  eventHandlers={{
                    click: () => setSelectedChallenge(challenge)
                  }}
                >
                  <Popup>
                    <div className="min-w-56">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {renderChallengeIcon(challenge, 24)}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{challenge.name}</h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${challengeTypes[challenge.type]?.color}20`, color: challengeTypes[challenge.type]?.color }}>{challengeTypes[challenge.type]?.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              challenge.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                              challenge.difficulty === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                              challenge.difficulty === 'Hard' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>{challenge.difficulty}</span>
                            {challenge.isDeleted && (
                              <span className="text-xs px-1.5 py-0.5 bg-red-200 text-red-700 rounded">Deleted</span>
                            )}
                            {!challenge.isDeleted && challenge.isActive === false && (
                              <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">Inactive</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3 line-clamp-3">{challenge.description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="font-semibold text-primary-600">{challenge.points} pts</span>
                        <span>Radius: {challenge.location.radius}m</span>
                        <span>{challenge.stats.completions} completions</span>
                        {challenge.totalRatings > 0 && (
                          <span className="text-amber-600 font-medium">★ {challenge.averageRating?.toFixed(1)}</span>
                        )}
                        {challenge.hiddenGem && (
                          <span className="text-pink-600 font-medium">💎 Gem</span>
                        )}
                      </div>
                      {challenge.challengeMode === 'multi_point' && (
                        <div className="flex items-center mt-2 text-xs text-indigo-600 border-t border-gray-100 pt-2">
                          <FiNavigation className="w-3 h-3 mr-1" />
                          <span>
                            {challenge.routeType === 'ordered' ? 'Ordered Route' :
                             challenge.routeType === 'unordered' ? 'Collect All' :
                             challenge.routeType === 'minimum' ? `Min ${challenge.routeMinRequired} waypoints` : 'Route'}
                          </span>
                        </div>
                      )}
                      {challenge.timeConstraint && (
                        <div className="flex items-center mt-2 text-xs text-purple-600 border-t border-gray-100 pt-2">
                          <FiClock className="w-3 h-3 mr-1" />
                          <span>Time restricted</span>
                        </div>
                      )}
                      {challenge.verificationType && challenge.verificationType !== 'gps' && (
                        <div className={`flex items-center mt-2 text-xs border-t border-gray-100 pt-2 gap-2 ${!challenge.timeConstraint ? 'mt-2' : ''}`}>
                          {['qr_code', 'gps_qr'].includes(challenge.verificationType) && (
                            <span className="flex items-center text-green-600"><FiGrid className="w-3 h-3 mr-1" />QR Code</span>
                          )}
                          {['passphrase', 'gps_passphrase'].includes(challenge.verificationType) && (
                            <span className="flex items-center text-amber-600"><FiKey className="w-3 h-3 mr-1" />Passphrase</span>
                          )}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
                
                {/* Challenge radius circle */}
                <Circle
                  center={[challenge.location.latitude, challenge.location.longitude]}
                  radius={challenge.location.radius}
                  pathOptions={{
                    fillColor: challengeTypes[challenge.type]?.color || '#45B7D1',
                    fillOpacity: 0.1,
                    color: challengeTypes[challenge.type]?.color || '#45B7D1',
                    weight: 2
                  }}
                />
              </React.Fragment>
            ))}

            {/* Route waypoint visualization for selected route challenge */}
            {selectedChallenge?.challengeMode === 'multi_point' && routeWaypointsCache[selectedChallenge.id] && (
              <>
                {/* Polyline connecting waypoints */}
                <Polyline
                  positions={routeWaypointsCache[selectedChallenge.id].map(wp => [wp.latitude, wp.longitude])}
                  pathOptions={{
                    color: '#6366F1',
                    weight: 3,
                    opacity: 0.7,
                    dashArray: selectedChallenge.routeType === 'ordered' ? null : '8, 8'
                  }}
                />
                {/* Numbered waypoint markers */}
                {routeWaypointsCache[selectedChallenge.id].map((wp, idx) => (
                  <React.Fragment key={`wp-${wp.id}`}>
                    <Marker
                      position={[wp.latitude, wp.longitude]}
                      icon={L.divIcon({
                        className: 'route-waypoint-icon',
                        html: `<div style="background:#6366F1;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,0.3);">${idx + 1}</div>`,
                        iconSize: [24, 24],
                        iconAnchor: [12, 12]
                      })}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-medium">{wp.name || `Waypoint ${idx + 1}`}</div>
                          {wp.description && <p className="text-xs text-gray-600 mt-1">{wp.description}</p>}
                          <div className="text-xs text-gray-500 mt-1">Radius: {wp.radius}m</div>
                        </div>
                      </Popup>
                    </Marker>
                    <Circle
                      center={[wp.latitude, wp.longitude]}
                      radius={wp.radius || 30}
                      pathOptions={{
                        fillColor: '#6366F1',
                        fillOpacity: 0.08,
                        color: '#6366F1',
                        weight: 1,
                        dashArray: '4, 4'
                      }}
                    />
                  </React.Fragment>
                ))}
              </>
            )}

            {/* Waypoint pick-from-map mode markers */}
            {isPickingWaypoints && pickedWaypoints.map((wp, idx) => (
              <Marker
                key={`pick-${idx}`}
                position={[wp.lat, wp.lng]}
                icon={L.divIcon({
                  className: 'pick-waypoint-icon',
                  html: `<div style="background:#6366F1;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${idx + 1}</div>`,
                  iconSize: [28, 28],
                  iconAnchor: [14, 14]
                })}
              />
            ))}

            {/* Temporary repositioning marker */}
            {tempMarkerLocation && isRepositioningMarker && (
              <>
                <Marker 
                  position={tempMarkerLocation}
                  ref={(marker) => {
                    if (marker) {
                      // Open popup immediately when marker is created
                      setTimeout(() => {
                        if (!marker.isPopupOpen()) {
                          marker.openPopup();
                        }
                      }, 100);
                    }
                  }}
                  icon={L.divIcon({
                    className: 'custom-div-icon',
                    html: `<div style="background-color: #3B82F6; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
                    iconSize: [30, 30],
                    iconAnchor: [15, 15]
                  })}
                >
                  <Popup closeButton={false} autoClose={false} closeOnClick={false}>
                    <div className="p-2" style={{ minWidth: '200px' }}>
                      <div className="text-center mb-3">
                        <div className="font-semibold text-gray-900">New Location</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {tempMarkerLocation[0].toFixed(5)}, {tempMarkerLocation[1].toFixed(5)}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            // Store the new location  
                            const newLocation = tempMarkerLocation;
                            setRepositionedLocation(newLocation);
                            setShowLocationConfirm(false);
                            setIsRepositioningMarker(false);
                            setTempMarkerLocation(null);
                            
                            // Use the stored repositioning challenge
                            if (repositioningChallenge) {
                              // Update the challenge with new location before setting it
                              const updatedChallenge = {
                                ...repositioningChallenge,
                                location: {
                                  ...repositioningChallenge.location,
                                  latitude: newLocation[0],
                                  longitude: newLocation[1]
                                }
                              };
                              setEditingChallenge(updatedChallenge);
                              setShowEditModal(true);
                              toast.success('Location updated! Remember to save.');
                            } else {
                              console.warn('No repositioning challenge found');
                              toast.error('Please try again from the challenge list');
                            }
                          }}
                          className="btn btn-primary flex-1 text-sm transition-colors font-medium"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => {
                            setTempMarkerLocation(null);
                            // Keep repositioning mode active
                          }}
                          className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
                <Circle
                  center={tempMarkerLocation}
                  radius={100}
                  pathOptions={{
                    fillColor: '#3B82F6',
                    fillOpacity: 0.2,
                    color: '#3B82F6',
                    weight: 2
                  }}
                />
              </>
            )}

            {/* New challenge location marker */}
            {showCreateModal && newChallengeLocation && (
              <>
                <Marker position={newChallengeLocation}>
                  <Popup>
                    <div className="text-center">
                      <div className="text-lg mb-1">📍</div>
                      <div className="font-medium">New Challenge Location</div>
                      <div className="text-sm text-gray-600">
                        {newChallengeLocation[0].toFixed(6)}, {newChallengeLocation[1].toFixed(6)}
                      </div>
                    </div>
                  </Popup>
                </Marker>
                <Circle
                  center={newChallengeLocation}
                  radius={100}
                  pathOptions={{
                    fillColor: 'green',
                    fillOpacity: 0.2,
                    color: 'green',
                    weight: 2,
                    dashArray: '5, 5'
                  }}
                />
              </>
            )}
            </MapContainer>
            </div>

            {/* Location Status */}
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${userLocation ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span>GPS: {userLocation ? 'Active' : 'Inactive'}</span>
                </div>
                {locationAccuracy && (
                  <span>Accuracy: ±{Math.round(locationAccuracy)}m</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <FiLayers className="w-4 h-4" />
                  <span>OpenStreetMap</span>
                </div>
                <button
                  onClick={exportToCSV}
                  className="ml-3 px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center space-x-1.5 transition-colors"
                  title="Export filtered challenges to CSV"
                >
                  <FiDownload className="w-3.5 h-3.5" />
                  <span>Export Challenges</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge Detail Modal */}
      <ChallengeDetailModal
        isOpen={!!selectedChallenge}
        onClose={() => setSelectedChallenge(null)}
        challenge={selectedChallenge}
        challengeTypes={challengeTypes}
      />

      {/* Create Challenge Modal */}
      <CreateChallengeModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewChallengeLocation(null);
          // Reset form data to initial values on close
          setCreateChallengeFormData({
            name: '',
            type: 'urban',
            description: '',
            radius: 100,
            difficulty: 'Easy',
            points: '10',
            latitude: '',
            longitude: '',
            criteria: {},
            nftRewardTemplate: '',
            location: '',
            image_url: '',
            icon_emoji: '🏆',
            badge_color: '#E5E7EB'
          });
        }}
        location={newChallengeLocation}
        formData={createChallengeFormData}
        setFormData={setCreateChallengeFormData}
        onSubmit={(data) => {
          createChallengeMutation.mutate(data);
        }}
        isLoading={createChallengeMutation.isLoading}
        onChooseImage={() => {
          setImageGalleryTarget('create');
          setShowNFTImageGallery(true);
        }}
        onPickFromMap={(callback) => {
          setShowCreateModal(false);
          setIsPickingWaypoints(true);
          setPickedWaypoints([]);
          setWaypointPickCallback(() => (points) => {
            callback(points);
            setShowCreateModal(true);
          });
        }}
      />

      {/* Edit Challenge Modal */}
      <EditChallengeModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingChallenge(null);
        }}
        challenge={editingChallenge}
        externalFormData={editChallengeFormData}
      />

      {/* Challenge Refinement Modal */}
      {showRefinementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Refine Challenge Generation</h2>
                <p className="text-primary-100 mt-1">Specify region and parameters for AI suggestions</p>
              </div>
              <button
                onClick={() => setShowRefinementModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Key Insights from Statistical Analysis */}
              {keyInsights.length > 0 && (
                <div className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-primary-200 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <FiTarget className="w-5 h-5 mr-2 text-primary-600" />
                    Key Insights & Recommendations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {keyInsights.map((suggestion, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            suggestion.priority === 'high'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {suggestion.priority === 'high' ? 'HIGH PRIORITY' : 'RECOMMENDED'}
                          </span>
                          <span className="text-xs text-gray-500">{suggestion.estimatedEngagement}% engagement</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center mr-2">
                            <span className="text-white text-sm font-bold">
                              {suggestion.category.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 capitalize">{suggestion.category}</div>
                            <div className="text-xs text-gray-600">{suggestion.difficulty} difficulty</div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{suggestion.reason}</p>
                        <div className="mt-2 text-xs font-semibold text-primary-600">{suggestion.recommendedPoints} pts recommended</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-primary-700 mt-3 flex items-center">
                    <FiZap className="w-3 h-3 mr-1" />
                    Configure filters below to focus AI generation on these opportunities
                  </p>
                </div>
              )}

              {/* Region Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location / Region
                </label>
                <input
                  type="text"
                  value={regionInput}
                  onChange={(e) => setRegionInput(e.target.value)}
                  placeholder="e.g., Hamburg Hbf, Alexanderplatz Berlin"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a street, landmark, neighborhood, or city</p>
              </div>

              {/* Country Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <select
                  value={countryInput}
                  onChange={(e) => setCountryInput(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  <option value="Germany">Germany</option>
                  <option value="Austria">Austria</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Belgium">Belgium</option>
                  <option value="France">France</option>
                  <option value="Italy">Italy</option>
                  <option value="Spain">Spain</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="USA">USA</option>
                  <option value="">Other (specify in location)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Select country to ensure accurate location matching</p>
              </div>

              {/* Radius Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Radius (optional)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={radiusKm}
                    onChange={(e) => setRadiusKm(e.target.value)}
                    placeholder="e.g., 10"
                    min="1"
                    max="50"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <span className="text-gray-600 font-medium">km</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Leave empty for general region, or specify radius (e.g., "10" for 10km)</p>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Challenge Categories (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.filter(cat => cat.slug !== 'general').map(category => (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (selectedCategories.includes(category.slug)) {
                          setSelectedCategories(selectedCategories.filter(c => c !== category.slug));
                        } else {
                          setSelectedCategories([...selectedCategories, category.slug]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${
                        selectedCategories.includes(category.slug)
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={selectedCategories.includes(category.slug) ? { backgroundColor: category.color } : {}}
                    >
                      <LucideIcon name={category.icon} size={14} />
                      {category.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedCategories.length > 0
                    ? `Selected: ${selectedCategories.join(', ')}`
                    : 'Leave empty for all categories'
                  }
                </p>
              </div>

              {/* Difficulty Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Levels (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Easy', 'Moderate', 'Hard', 'Expert'].map(difficulty => (
                    <button
                      key={difficulty}
                      onClick={() => {
                        if (selectedDifficulties.includes(difficulty)) {
                          setSelectedDifficulties(selectedDifficulties.filter(d => d !== difficulty));
                        } else {
                          setSelectedDifficulties([...selectedDifficulties, difficulty]);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        selectedDifficulties.includes(difficulty)
                          ? difficulty === 'Easy'
                            ? 'bg-green-600 text-white'
                            : difficulty === 'Moderate'
                            ? 'bg-yellow-600 text-white'
                            : difficulty === 'Hard'
                            ? 'bg-orange-600 text-white'
                            : 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedDifficulties.length > 0
                    ? `Selected: ${selectedDifficulties.join(', ')}`
                    : 'Leave empty for all difficulty levels'
                  }
                </p>
              </div>

              {/* Preview */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <FiZap className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900">Generation Preview</h3>
                    <p className="text-sm text-primary-800 mt-1">
                      {radiusKm && radiusKm.trim()
                        ? `Will generate up to 20 challenges within ${radiusKm}km of ${regionInput}${countryInput ? `, ${countryInput}` : ''}`
                        : `Will generate up to 20 challenges in ${regionInput}${countryInput ? `, ${countryInput}` : ''}`
                      }
                      {selectedCategories.length > 0 && (
                        <span> focusing on: <strong>{selectedCategories.join(', ')}</strong></span>
                      )}
                      {selectedDifficulties.length > 0 && (
                        <span> with difficulty: <strong>{selectedDifficulties.join(', ')}</strong></span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowRefinementModal(false)}
                disabled={generating}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={handleGenerateChallenges}
                disabled={!regionInput.trim() || generating}
                className="btn btn-primary transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center space-y-1"
              >
                {generating ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <FiInfo className="w-5 h-5" />
                      <span>{t('cms.map.generating')}</span>
                    </div>
                    <span className="text-xs text-white/80">This may take up to 2 minutes</span>
                  </>
                ) : (
                  <>
                    <FiZap className="w-5 h-5" />
                    <span>{t('cms.map.generateWithAi')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingChallenge(null);
        }}
        challenge={deletingChallenge}
      />

      {/* Permanent Delete Confirmation Modal */}
      <PermanentDeleteConfirmationModal
        isOpen={showPermanentDeleteModal}
        onClose={() => {
          setShowPermanentDeleteModal(false);
          setPermanentDeletingChallenge(null);
        }}
        challenge={permanentDeletingChallenge}
      />

      {/* Challenge Generator Modal */}
      {showGeneratorModal && generatedSuggestions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center">
                  Challenge Suggestions
                  {generatedSuggestions.aiEnabled && (
                    <span className="ml-3 bg-white text-primary-600 px-2 py-1 rounded text-xs font-medium">AI-Powered</span>
                  )}
                </h2>
                <p className="text-primary-100 mt-1">
                  {generatedSuggestions.aiEnabled
                    ? 'AI-generated real challenges based on statistical analysis'
                    : 'Data-driven recommendations based on user behavior'
                  }
                </p>
              </div>
              <button
                onClick={() => setShowGeneratorModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Analysis Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-primary-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">{t('cms.map.totalChallenges')}</div>
                  <div className="text-2xl font-bold text-primary-600">{generatedSuggestions.analysis.totalExistingChallenges}</div>
                </div>
                <div className="bg-primary-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Avg Completion Rate</div>
                  <div className="text-2xl font-bold text-primary-600">{generatedSuggestions.analysis.averageCompletionRate}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Most Popular</div>
                  <div className="text-lg font-bold text-green-600">{generatedSuggestions.analysis.mostPopularCategory}</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Least Popular</div>
                  <div className="text-lg font-bold text-orange-600">{generatedSuggestions.analysis.leastPopularCategory}</div>
                </div>
              </div>

              {/* Suggestion Banner (when fewer challenges found than expected) */}
              {generatedSuggestions.suggestion && (
                <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        {generatedSuggestions.suggestion.message}
                      </p>
                      <div className="mt-2">
                        <p className="text-sm text-yellow-700 font-semibold mb-1">Try:</p>
                        <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                          {generatedSuggestions.suggestion.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI-Generated Real Challenges */}
              {generatedSuggestions.aiEnabled && generatedSuggestions.aiChallenges && generatedSuggestions.aiChallenges.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <span className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-1 rounded-full text-sm mr-3">AI</span>
                      Real Challenge Suggestions
                    </h3>
                    <span className="text-sm text-gray-500">{generatedSuggestions.region}</span>
                  </div>

                  {/* Search Area Info */}
                  {generatedSuggestions.searchArea && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="flex items-center text-blue-700">
                            <FiMapPin className="w-4 h-4 mr-1" />
                            <strong>Search Center:</strong>
                            <span className="ml-1 font-mono">
                              {generatedSuggestions.searchArea.center.lat.toFixed(4)}, {generatedSuggestions.searchArea.center.lng.toFixed(4)}
                            </span>
                          </span>
                          <span className="text-blue-600">
                            <strong>Radius:</strong> {generatedSuggestions.searchArea.radiusKm} km
                          </span>
                          <span className="text-blue-600">
                            <strong>POIs found:</strong> {generatedSuggestions.searchArea.poisFound}
                          </span>
                        </div>
                        <button
                          onClick={() => setShowSuggestionsMap(!showSuggestionsMap)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
                        >
                          <FiMap className="w-4 h-4 mr-1" />
                          {showSuggestionsMap ? 'Hide Map' : 'Show Map'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Map Preview of Generated Challenges */}
                  {showSuggestionsMap && generatedSuggestions.searchArea && (
                    <div className="mb-4 rounded-lg overflow-hidden border border-gray-300" style={{ height: '300px' }}>
                      <MapContainer
                        center={[generatedSuggestions.searchArea.center.lat, generatedSuggestions.searchArea.center.lng]}
                        zoom={12}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {/* Search radius circle */}
                        <Circle
                          center={[generatedSuggestions.searchArea.center.lat, generatedSuggestions.searchArea.center.lng]}
                          radius={generatedSuggestions.searchArea.radiusKm * 1000}
                          pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.1 }}
                        />
                        {/* Center marker */}
                        <Marker
                          position={[generatedSuggestions.searchArea.center.lat, generatedSuggestions.searchArea.center.lng]}
                          icon={L.divIcon({
                            className: 'custom-div-icon',
                            html: '<div style="background-color: #3b82f6; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                            iconSize: [16, 16],
                            iconAnchor: [8, 8]
                          })}
                        >
                          <Popup>Search Center: {generatedSuggestions.searchArea.location}</Popup>
                        </Marker>
                        {/* Challenge markers */}
                        {generatedSuggestions.aiChallenges.map((challenge, idx) => (
                          <Marker
                            key={idx}
                            position={[challenge.latitude, challenge.longitude]}
                            icon={L.divIcon({
                              className: 'custom-div-icon',
                              html: `<div style="background-color: #10b981; color: white; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">${idx + 1}</div>`,
                              iconSize: [24, 24],
                              iconAnchor: [12, 12]
                            })}
                          >
                            <Popup>
                              <strong>{challenge.name}</strong><br/>
                              {challenge.category} | {challenge.difficulty} | {challenge.points} pts
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {generatedSuggestions.aiChallenges
                      .map((challenge, index) => ({ challenge, index }))
                      .filter(({ index }) => !addedChallengeIndices.has(index))
                      .map(({ challenge, index }) => (
                      <div key={index} className="bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-md font-bold text-gray-900 flex-1">{challenge.name}</h4>
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                            challenge.difficulty === 'Easy'
                              ? 'bg-green-100 text-green-800'
                              : challenge.difficulty === 'Moderate'
                              ? 'bg-yellow-100 text-yellow-800'
                              : challenge.difficulty === 'Hard'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {challenge.difficulty}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 mb-3">{challenge.description}</p>

                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <div className="flex items-center space-x-3">
                            <span className="bg-white px-2 py-1 rounded capitalize">{challenge.category}</span>
                            <span className="font-semibold text-primary-600">{challenge.points} pts</span>
                          </div>
                          <span className="text-gray-500 flex items-center gap-1"><FiMapPin className="w-3 h-3" /> {challenge.latitude.toFixed(4)}, {challenge.longitude.toFixed(4)}</span>
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-gray-500">{challenge.reason}</span>
                          <div className="flex items-center text-xs text-gray-500">
                            <span className="mr-1">Engagement:</span>
                            <span className="font-semibold text-primary-600">{challenge.estimatedEngagement}%</span>
                          </div>
                        </div>

                        {/* Edit and Add Buttons */}
                        <div className="mt-3 flex space-x-2">
                          <button
                            onClick={() => handleEditGeneratedChallenge(challenge, index)}
                            className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm flex items-center justify-center space-x-2 text-sm font-medium"
                          >
                            <FiEdit2 className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleAddChallenge(challenge, index)}
                            className="flex-1 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-sm flex items-center justify-center space-x-2 text-sm font-medium"
                          >
                            <FiPlus className="w-4 h-4" />
                            <span>Add</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add All Button */}
                  {(() => {
                    const remainingCount = generatedSuggestions.aiChallenges.length - addedChallengeIndices.size;
                    return (
                      <div className="mt-4 flex items-center justify-between gap-4">
                        <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-start">
                            <FiZap className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-green-800">
                              <strong>AI-Generated:</strong> These are real challenge suggestions with actual locations, coordinates, and descriptions generated by Claude.
                              {addedChallengeIndices.size > 0 && (
                                <span className="block mt-1 font-semibold text-green-900 flex items-center gap-1">
                                  <FiCheck className="w-4 h-4" /> {addedChallengeIndices.size} already added • {remainingCount} remaining
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        {remainingCount > 0 && (
                          <button
                            onClick={handleAddAllChallenges}
                            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md flex items-center space-x-2 font-semibold whitespace-nowrap"
                          >
                            <FiPlus className="w-5 h-5" />
                            <span>Add All {remainingCount} Remaining</span>
                          </button>
                        )}
                        {remainingCount === 0 && (
                          <div className="px-6 py-3 bg-gray-100 text-gray-500 rounded-lg flex items-center space-x-2 font-semibold whitespace-nowrap">
                            <FiCheck className="w-5 h-5" />
                            <span>All Challenges Added</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Possible Duplicates - Below Main Suggestions */}
              {generatedSuggestions.duplicates && generatedSuggestions.duplicates.count > 0 && (
                <div className="mb-6">
                  <div className="bg-primary-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-3">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-primary-800">
                          Possible Duplicates / Already in the Database
                        </p>
                        <p className="text-xs text-primary-600 mt-1">
                          {generatedSuggestions.duplicates.count} potential duplicate{generatedSuggestions.duplicates.count > 1 ? 's' : ''} filtered out. Review and add them if they're not actually duplicates.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Filtered Duplicates List */}
                  <div className="space-y-3">
                    {generatedSuggestions.duplicates.challenges.map((duplicate, index) => (
                      <div key={index} className="bg-white border border-primary-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900">{duplicate.name}</h5>
                                <p className="text-sm text-gray-600 mt-1">{duplicate.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span className={`px-2 py-0.5 rounded ${
                                    duplicate.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                                    duplicate.difficulty === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                                    duplicate.difficulty === 'Hard' ? 'bg-orange-100 text-orange-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {duplicate.difficulty}
                                  </span>
                                  <span className="text-primary-600">{duplicate.category}</span>
                                  <span>{duplicate.points} pts</span>
                                </div>

                                {/* Similarity Info */}
                                <div className="mt-3 bg-primary-50 rounded p-2 text-xs">
                                  <p className="text-primary-800">
                                    <strong>Similar to existing:</strong> <span className="font-medium">{duplicate.matchedWith.name}</span>
                                  </p>
                                  <p className="text-primary-600 mt-1 flex items-center gap-1">
                                    <FiMapPin className="w-3 h-3" /> Distance: ~{Math.round(
                                      Math.sqrt(
                                        Math.pow((duplicate.latitude - duplicate.matchedWith.latitude) * 111000, 2) +
                                        Math.pow((duplicate.longitude - duplicate.matchedWith.longitude) * 111000 * Math.cos(duplicate.latitude * Math.PI / 180), 2)
                                      )
                                    )}m apart
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Add Anyway Button */}
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => handleAddChallenge(duplicate, `dup-${index}`)}
                              disabled={addedChallengeIndices.has(`dup-${index}`)}
                              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                addedChallengeIndices.has(`dup-${index}`)
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-secondary-600 text-white hover:bg-secondary-700 shadow-md hover:shadow-lg'
                              }`}
                            >
                              {addedChallengeIndices.has(`dup-${index}`) ? <><FiCheck className="w-4 h-4 inline mr-1" /> Added</> : 'Add Anyway'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!generatedSuggestions.aiEnabled && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <FiZap className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-yellow-900">AI Challenge Generation Disabled</h3>
                      <p className="text-sm text-yellow-800 mt-1">
                        Add your Anthropic API key to <code className="bg-yellow-100 px-1 rounded">.env</code> file to enable AI-powered real challenge generation with Claude.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Suggestions Table - Removed, showing only AI challenges */}
              {false && <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Engagement</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {generatedSuggestions.suggestions.map((suggestion, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              suggestion.priority === 'high'
                                ? 'bg-red-100 text-red-800'
                                : suggestion.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {suggestion.priority.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-white text-sm font-bold">
                                  {suggestion.category.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900 capitalize">{suggestion.category}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              suggestion.difficulty === 'easy'
                                ? 'bg-green-100 text-green-800'
                                : suggestion.difficulty === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {suggestion.difficulty}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="font-semibold">{suggestion.recommendedPoints}</span> pts
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2 mr-2" style={{ width: '100px' }}>
                                <div
                                  className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                                  style={{ width: `${Math.min(suggestion.estimatedEngagement, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{suggestion.estimatedEngagement}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {suggestion.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>}

            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end border-t border-gray-200">
              <button
                onClick={() => setShowGeneratorModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Generated Challenge Modal */}
      {showEditGeneratedModal && editingGeneratedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">{t('cms.map.challengeDetails')}</h2>
                <p className="text-blue-100 mt-1">Modify details before adding to database</p>
              </div>
              <button
                onClick={() => {
                  setShowEditGeneratedModal(false);
                  setEditingGeneratedChallenge(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Challenge Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Challenge Name</label>
                <input
                  type="text"
                  value={editingGeneratedChallenge.name}
                  onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingGeneratedChallenge.description}
                  onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Category and Difficulty */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={editingGeneratedChallenge.category}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="urban">Urban</option>
                    <option value="nature">Nature</option>
                    <option value="historic">Historic</option>
                    <option value="adventure">Adventure</option>
                    <option value="water">Water</option>
                    <option value="park">Park</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('cms.map.difficulty')}</label>
                  <select
                    value={editingGeneratedChallenge.difficulty}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, difficulty: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Hard">Hard</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
              </div>

              {/* Points and Radius */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Points</label>
                  <input
                    type="number"
                    value={editingGeneratedChallenge.points}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, points: parseInt(e.target.value) || 10})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('cms.map.radius')}</label>
                  <input
                    type="number"
                    value={editingGeneratedChallenge.radius}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, radius: parseInt(e.target.value) || 50})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* GPS Coordinates */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('cms.map.coordinates')}</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingGeneratedChallenge.latitude}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, latitude: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingGeneratedChallenge.longitude}
                    onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, longitude: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* NFT Visual Customization */}
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 mb-4 border border-primary-200">
                <h3 className="text-sm font-bold text-primary-900 mb-3 flex items-center">
                  <FiImage className="w-5 h-5 text-primary-600 mr-2" />
                  NFT Visual Customization
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Location/Region */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('cms.map.location')}
                      <span className="text-gray-500 ml-1">(e.g., Berlin, Vienna)</span>
                    </label>
                    <input
                      type="text"
                      value={editingGeneratedChallenge.location || ''}
                      onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, location: e.target.value})}
                      placeholder="City or region name"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">For template matching</p>
                  </div>

                  {/* Icon Gallery - Show preview or default */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      NFT Icon
                      <span className="text-gray-500 ml-1">(optional)</span>
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-gray-300">
                        {editingGeneratedChallenge.icon_emoji ? (
                          editingGeneratedChallenge.icon_emoji.startsWith('http') ? (
                            <img src={editingGeneratedChallenge.icon_emoji} alt="icon" className="w-12 h-12 object-contain" />
                          ) : (
                            <span className="text-3xl">{editingGeneratedChallenge.icon_emoji}</span>
                          )
                        ) : (
                          <span className="text-3xl">🏆</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIconGalleryTarget('generated');
                          setShowNFTIconGallery(true);
                        }}
                        className="btn btn-primary text-sm"
                      >
                        Choose Icon
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {/* NFT Image Gallery - Show preview or placeholder */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      NFT Image
                      <span className="text-gray-500 ml-1">(optional)</span>
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-gray-300 overflow-hidden">
                        {editingGeneratedChallenge.image_url ? (
                          <img src={editingGeneratedChallenge.image_url} alt="NFT preview" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-400 text-xs text-center px-2">No image</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImageGalleryTarget('generated');
                          setShowNFTImageGallery(true);
                        }}
                        className="btn btn-primary text-sm"
                      >
                        Choose Image
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Custom NFT image (highest priority)</p>
                  </div>

                  {/* Badge Color */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Badge Color
                      <span className="text-gray-500 ml-1">(optional)</span>
                    </label>
                    <input
                      type="color"
                      value={editingGeneratedChallenge.badge_color || '#3B82F6'}
                      onChange={(e) => setEditingGeneratedChallenge({...editingGeneratedChallenge, badge_color: e.target.value})}
                      className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Geocoded Location Info */}
              {editingGeneratedChallenge.geocoded && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <FiTarget className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-green-900">Verified Coordinates</p>
                      <p className="text-xs text-green-700 mt-1">
                        {editingGeneratedChallenge.geocodedLocation || 'Coordinates verified via geocoding'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowEditGeneratedModal(false);
                  setEditingGeneratedChallenge(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('cms.common.cancel')}
              </button>
              <button
                onClick={handleSaveEditedChallenge}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md flex items-center space-x-2"
              >
                <FiSave className="w-5 h-5" />
                <span>{t('cms.common.save')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Image Picker (same as Cartoonify batch process uses) */}
      <GalleryImagePicker
        isOpen={showNFTImageGallery}
        onClose={() => {
          setShowNFTImageGallery(false);
          setImageGalleryTarget(null);
        }}
        onSelect={(imageUrl) => {
          if (imageGalleryTarget === 'edit') {
            setEditChallengeFormData(prev => ({ ...prev, image_url: imageUrl }));
          } else if (imageGalleryTarget === 'generated') {
            setEditingGeneratedChallenge({...editingGeneratedChallenge, image_url: imageUrl});
          } else if (imageGalleryTarget === 'create') {
            setCreateChallengeFormData(prev => ({ ...prev, image_url: imageUrl }));
          }
          setShowNFTImageGallery(false);
          setImageGalleryTarget(null);
        }}
        defaultSource="challenges"
      />

      {/* NFT Icon Gallery Modal */}
      <NFTIconGalleryModal
        isOpen={showNFTIconGallery}
        onClose={() => {
          setShowNFTIconGallery(false);
          setIconGalleryTarget(null);
        }}
        onSelectIcon={(iconUrl) => {
          if (iconGalleryTarget === 'edit') {
            setEditChallengeFormData(prev => ({ ...prev, icon_emoji: iconUrl }));
          } else if (iconGalleryTarget === 'generated') {
            setEditingGeneratedChallenge({...editingGeneratedChallenge, icon_emoji: iconUrl});
          } else if (iconGalleryTarget === 'create') {
            setCreateChallengeFormData(prev => ({ ...prev, icon_emoji: iconUrl }));
          }
          setShowNFTIconGallery(false);
          setIconGalleryTarget(null);
        }}
      />
    </div>
  );
};

export default Map;